<template>
  <v-container fluid class="">
    <div class="d-flex align-center justify-space-between">
      <h1>Settings</h1>
      <HelpMenu />
    </div>
    <SettingsMenu
      v-if="!isInitializing"
      :items="settingOptions"
      :value="selectedTab"
      class="ex-menu-div"
      data-test="setting-menu"
      @onChange="handleTabChange"
      @onSameTabClick="handleSameTabClick"
    >
    </SettingsMenu>
    <v-tabs-items
      v-if="!isInitializing"
      v-model="selectedTab"
      class="custom-tabs custom-tabs-shadow mt-4"
    >
      <v-tab-item :transition="false" v-for="(item, index) in settingOptions" :key="index">
        <v-card :elevation="isFlatCard ? 0 : 1" :class="!isFlatCard ? 'elevated-card' : ''">
          <v-card-text
            class="custom-view"
            v-if="settingOptions.findIndex((el) => el.title == 'Users') == selectedTab"
          >
            <div class="px-5 mb-5">
              <SearchBar
                type="button"
                ghostText="Search"
                :add="true"
                btnText="Add user"
                @onClick="onUsersSearchBarClick"
                @keydown.native="onUsersSearchBarKeydown"
                @onSearch="onFilter"
                @removeFilter="removeFilter"
                :label="usersSearchBarFiltersLabel"
                :length="appliedFilters.length"
                :chipText="appliedFilters"
                :valueSearch="filterText"
              />
            </div>
            <SearchModal
              :dialog="showUsersFilterModal"
              @back="showUsersFilterModal = false"
              filterType="Users"
              @checkedItemsChanged="onUsersFilterModalApply"
              :clearSelection="clearSelection"
              :filterGroups="effectiveUsersFilterGroups"
              :statusOptions="usersStatusOptions"
              :modalWidth="620"
              :defaultSelected="appliedFilters"
            />
            <ExCreateEmployeeWizard
              :accessLevel="accessLevel"
              @created="onCreatedUser"
              :isDialogVisible="isAddDialogVisible"
              @closeAddDialog="isAddDialogVisible = false"
            />
            <div v-if="userListError" class="users-list-error mt-5" role="alert">
              <EmptyState
                icon="alert-circle"
                title="Unable to load users"
                supportingText="We couldn't load the users list. Check your connection and try again."
              />
              <Button
                class="users-list-error__retry"
                outline="outline-default"
                label="Retry"
                :backgroundColor="'#ffffff'"
                size="medium"
                @onClick="retryUserList"
              />
            </div>
            <DataTable
              class="user-tr mt-5"
              :pagination="paginationModel"
              :headers="headers"
              :data="filteredUsers"
              :currentSort="sortOptions"
              @onRequest="requestData"
              @onSort="handleSort"
              :no-hover-effect="true"
              :serverPaginated="settingsUserV2Flags"
            >
              <template #no-data> </template>
              <template #user="{ row }">
                <UserCard
                  :fullWidth="true"
                  :picture="row.user.data?.avatar"
                  :ideaMdp432Enabled="ideaMdp432AddUserWizardEnabled"
                  :employeeStatus="userCardEmployeeStatus(row)"
                  :name="`${row.user.data.user.first_name} ${row.user.data.user.last_name}`"
                  :email="`${row.user.data.user.email}`"
                  :userStatus="row.user.data.user.state"
                  :position="row.user.data.position"
                  :showPosition="true"
                  :showUnlinkButton="
                    ideaMdp453NewGiveAccessToEmployee ? row.user.data.has_employee : false
                  "
                  :hasEmployee="row.user.data.has_employee"
                  :user="{
                    id: row.user.data.user.id,
                    name: row.user.data.user.first_name + ' ' + row.user.data.user.last_name,
                    email: row.user.data.user.email,
                    avatar: row.user.data?.avatar,
                    linkedToEmployee: false,
                    lostAccount:
                      row.user.data.user.state == 'suspended' ||
                      row.user.data.user.state == 'disabled',
                    employeeStatus: row.user.data.user.state,
                    userStatus: row.user.data.user.state_str,
                  }"
                  :employee="{
                    id: row.user.data.employee?.id,
                    name: `${row.user.data.employee?.first_name} ${row.user.data.employee?.last_name}`,
                    avatar: row.user.data.employee?.avatar
                      ? row.user.data.employee?.avatar
                      : row.user.data?.avatar,
                  }"
                  @unlink-employee="openDetachEmployeeAccessDialog(row)"
                ></UserCard>
                <EXTooltip
                  v-if="inviteLifecycleEnabled && pendingEmailChangeLines(row.user.data)"
                  :title="pendingEmailChangeLines(row.user.data).tooltip.title"
                  :description="pendingEmailChangeLines(row.user.data).tooltip.description"
                  placement="top"
                  :delay="100"
                  showOnChildFocus
                >
                  <span class="pending-email" tabindex="0" data-test="pending-email-change">
                    <span class="pending-email__primary">{{
                      pendingEmailChangeLines(row.user.data).primary
                    }}</span>
                    <span
                      v-if="pendingEmailChangeLines(row.user.data).secondary"
                      class="pending-email__secondary"
                      >{{ pendingEmailChangeLines(row.user.data).secondary }}</span
                    >
                  </span>
                </EXTooltip>
              </template>
              <template #access="{ row }">
                <UserPermissionBadge
                  v-if="displayAdminPermission(row.user.data.admin_permission)"
                  :permissionType="displayAdminPermission(row.user.data.admin_permission)"
                  :appDropdown="displayAdminPermission(row.user.data.admin_permission)"
                  :IsEnabled="true"
                />
              </template>
              <template v-if="inviteLifecycleEnabled" #invite="{ row }">
                <div v-if="isInviteRow(row.user.data)" class="invite-cell">
                  <span class="invite-cell__primary">{{
                    getInviteCellLines(row.user.data).primary
                  }}</span>
                  <span
                    v-if="getInviteCellLines(row.user.data).secondary"
                    class="invite-cell__secondary"
                    >{{ getInviteCellLines(row.user.data).secondary }}</span
                  >
                </div>
              </template>
              <template #lastlogin="{ row }">
                <div
                  style="min-width: 280px"
                  v-if="
                    row.user.data.user.last_invited_at && row.user.data.user.state === 'invited'
                  "
                >
                  <UserActivity
                    :last_invited_at="row.user.data.user.last_invited_at"
                    :date_of_invite="row.user.data.user.date_of_invite"
                  />
                </div>
                <div style="min-width: 280px" v-else>
                  <span v-if="row.user.data.user.state === 'invited'">
                    {{ getUserStateLabel(row.user.data.user.state) }}
                    {{ formatDate(row.user.data.user.date_of_invite) }}
                  </span>
                  <span v-else>
                    {{
                      row.user.data.user.last_login != null
                        ? getUserStateLabel(row.user.data.user.state) +
                          formatLastLoginDate(row.user.data.user.last_login)
                        : ""
                    }}
                  </span>
                </div>
              </template>
              <template #status="{ row }">
                <div>
                  <EXTooltip
                    v-if="
                      inviteLifecycleEnabled &&
                      isInviteRow(row.user.data) &&
                      getInviteTooltip(row.user.data)
                    "
                    :title="getInviteTooltip(row.user.data).title"
                    :description="getInviteTooltip(row.user.data).description"
                    placement="top"
                    :delay="100"
                  >
                    <AccessStatusBadge
                      accessStatus="invited"
                      :inviteStage="getInviteStageKey(row.user.data.invite_stage)"
                      :actionRequired="needsAttention(row.user.data)"
                    />
                  </EXTooltip>
                  <AccessStatusBadge
                    v-else-if="ideaMdp398AccountInvitesRealtimeEnabled"
                    :status="getAccessStatusBadgeStatusFromRow(row.user.data)"
                  />
                  <UserStatusBadge
                    v-else
                    :access="row.user.data.user.state"
                    :status="getUserAccess(row.user.data.user.state, row.user.data.is_suspended)"
                    :invite_status="getUserStatusBadgeInviteStatusFromRow(row.user.data)"
                  />
                </div>
              </template>
              <template v-if="flags['users-mdp-344']" #chatter="{ row }">
                <div style="display: flex; justify-content: center">
                  <div class="chatter-icon-container" @click="openChatter(row)">
                    <Icon name="badge_holder_chatter_icon" />
                  </div>
                </div>
              </template>
              <template #actions="{ row }">
                <div class="actions-cell-wrapper">
                  <DropDownMenu
                    v-if="!['closed', 'blocked'].includes(row.user.data.user.state)"
                    @onClick="handleDropdownClick($event, row)"
                    :is-open="false"
                    :dropdown-items="getUsersRowMenuItems(row.user.data)"
                    :translateXOffset="0"
                    :translateYOffset="0"
                    :width="ideaMdp432AddUserWizardEnabled ? 234 : 195"
                  >
                    <template #activator>
                      <div class="settings-icon-container">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="icon-svg"
                        >
                          <path
                            d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                    </template>
                  </DropDownMenu>
                  <!-- Disabled state for closed/blocked users -->
                  <div v-else class="icon-container disabled">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon-svg"
                    >
                      <path
                        d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </template>
            </DataTable>
            <NoRecordFound
              v-if="showUsersFilterEmptyBanner"
              :title="title"
              :message="title"
              :btnTitle="'User'"
              :subtitle="''"
              class="no-record-found"
              :shouldShowAddAction="true"
              @onAction="isAddDialogVisible = true"
              :showClearAction="hasUsersActiveSearchOrFilters"
              :clearLabel="clearBtnLabel"
              @onClear="clearSearchFilter()"
              :description="emptyRecordDescription"
              :noBorder="true"
            />
          </v-card-text>
          <v-card-text
            v-if="settingOptions.findIndex((el) => el.title == 'Configurations') == selectedTab"
          >
            <ConfigurationView
              ref="configurationView"
              @update-shadow="onUpdateShadow"
              :dataSource="availableApps"
              :currencyList="currencyList"
              :currency-search-query="currencySearchQuery"
              :currency-list-total="currencyListTotal"
              :countryList="countryList"
              :country-search-query="countrySearchQuery"
              :country-list-total="countryListTotal"
              :dropdownCurrencyList="dropdownCurrencyList"
              :dropdownCountryList="dropdownCountryList"
              :operating-catalog-currency-ids="operatingCatalogCurrencyIds"
              :operating-catalog-country-ids="operatingCatalogCountryIds"
              @onAddCurrency="(c, s) => addCurrency(c, s)"
              @reloadData="getCurrencyList()"
              @currency-page-change="getCurrencyList"
              @currency-search="onCurrencySearch"
              @currency-status-filter="onCurrencyStatusFilter"
              @ensure-currency-catalog="onEnsureCurrencyCatalog"
              @set-rate="onSetCurrencyRate"
              @rate-mode-change="onCurrencyRateModeChange"
              @currency-menu-action="onCurrencyMenuAction"
              @bulk-set-mode="onBulkSetCurrencyMode"
              :currency-add-error-code="currencyAddErrorCode"
              @reloadCountryList="onReloadCountryList"
              @fetchOperatingCountry="getOperatingCountry()"
              @country-page-change="onCountryPageChange"
              @country-search="onCountrySearch"
              @country-status-filter="onCountryStatusFilter"
              @ensure-country-catalog="onEnsureCountryCatalog"
              @onAddCountry="onAddOperatingCountry"
              @set-work-hours="onSetWorkHours"
              @menu-action="onCountryMenuAction"
            />
          </v-card-text>
          <v-card-text
            v-if="settingOptions.findIndex((el) => el.title == 'Integrations') == selectedTab"
          >
            <IntegrationsAccessPanel
              v-if="ideaMdp51IntegrationsEnabled"
              v-slot="{ status, canManage, loading }"
            >
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="24"
                color="#7f56d9"
                aria-label="Loading integrations"
                data-testid="integrations-loading"
              />
              <SlackIntegrationCard
                v-else-if="status"
                :status="status.slack.status"
                :can-manage="canManage"
                :workspace-name="status.slack.workspace_name || ''"
                :connected-by="status.slack.connected_by"
                :connected-at="status.slack.connected_at || ''"
              />
              <TeamsIntegrationCard
                v-if="ideaMdp646TeamsEnabled && !loading && status"
                class="mt-4"
                :status="status.teams.status"
                :can-manage="canManage"
                :tenant-name="status.teams.tenant_name || ''"
                :connected-by="status.teams.connected_by"
                :connected-at="status.teams.connected_at || ''"
                :consent-url="status.teams.consent_url || ''"
                :reachable-count="status.teams.reachable_employee_count"
                :total-count="status.teams.total_employee_count"
              />
            </IntegrationsAccessPanel>
            <AppIntegrationsList v-else />
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <ExActionsPopUp
      v-if="userAccountActionDialogObject?.show ?? false"
      :headline="userAccountActionDialogObject?.title"
      :htmlInfo="userAccountActionDialogObject.description"
      :actionLabel="userAccountActionDialogObject?.actionText"
      :value="userAccountActionDialogObject?.show ?? false"
      :primary="userAccountActionDialogObject.primary ?? false"
      :termination="userAccountActionDialogObject.termination ?? false"
      :destructive="userAccountActionDialogObject.destructive ?? false"
      :avatar="{
        picture: '',
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      @onAction="OnActionUserConfirmationDialog"
      @input="OnCloseUserConfirmationDialog"
    />

    <ExActionsPopUp
      v-if="currencyNudge.show"
      :headline="currencyNudgeHeadline"
      :info="currencyNudgeInfo"
      actionLabel="Yes"
      cancelLabel="No"
      :value="currencyNudge.show"
      :termination="false"
      :deletePopup="false"
      :applyBorderClass="true"
      icon="currency_icon"
      @onAction="confirmCurrencyNudge"
      @input="declineCurrencyNudge"
    />

    <ChangeEmailModel
      v-if="selectedTab === 0 && showChangeEmailDialog"
      :showDialog="showChangeEmailDialog"
      :userList="userList"
      :currentUser="currentSelectedRow"
      :lifecycleEnabled="inviteLifecycleEnabled"
      @emailUpdated="emailUpdated"
      @closeAddDialog="showChangeEmailDialog = false"
    />

    <RenameUserNameModal
      :showDialog="showRenamePositionDialog"
      :user="{
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
        accountId: String(currentSelectedRow?.account) || '',
        userId: String(currentSelectedRow?.user?.id) || '',
      }"
      @closeDialog="() => (showRenamePositionDialog = false)"
      @reloadData="getUserList()"
    />
    <CancelInviteAdminUserPopup
      :show="showCancelInviteUserPopup"
      :user="{
        id: currentSelectedRow?.user?.id || 0,
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      :ownerAccess="inviteLifecycleEnabled && currentSelectedRow?.admin_permission === 'owner'"
      @onCancelInviteAdminUser="cancelInviteAdminUser"
      @onClose="showCancelInviteUserPopup = false"
    />

    <OwnerShipModal
      :dialog="showOnwerShipDialog"
      @onAction="transferOwner($event)"
      @back="onTransferOwnershipModalBack"
      @search-change="loadTransferOwnershipCandidateItems"
      @load-more-transfer-list="loadMoreTransferOwnershipCandidates"
      :items="items"
      :transfer-list-has-more="transferOwnershipHasMore"
      :transfer-list-loading-more="transferOwnershipLoadingMore"
    />
    <ChangeUserAccessDialog
      v-if="selectedTab === 0 && showChangeUserAccessDialog"
      :value="showChangeUserAccessDialog"
      :name="changeUserAccessDialogName"
      :avatar="changeUserAccessDialogAvatar"
      :user_line_admin_access="changeUserAccessDialogUserLine"
      :linked_to_employee="changeUserAccessDialogLinkedEmployee"
      :logged_in_user_admin_permission="changeUserAccessDialogLoggedInRole"
      @input="onChangeUserAccessDialogInput"
      @close="onChangeUserAccessDialogClose"
      @save="onSaveChangeUserAccess"
    />

    <DetachEmployeeAccessDialog
      v-if="selectedTab === 0 && showDetachEmployeeAccessDialog"
      :show="showDetachEmployeeAccessDialog"
      :avatar="detachEmployeeAccessDialogAvatar"
      :full_name="detachEmployeeAccessDialogFullName"
      @onClose="closeDetachEmployeeAccessDialog"
      @onAction="onDetachEmployeeAccessDialogAction"
    />

    <EditPermission
      :showDialog="dialogTitle1"
      :currentUser="currentSelectedRow ? currentSelectedRow : {}"
      :availableItems="appAccessTypes"
      :permissions="appPermissions"
      @closeAddDialog="dialogTitle1 = false"
      @onUpdatePermissions="onSaveAppAccess"
      @onUpdateAppPermissions="updateAppPermissions"
    />

    <ChecklistDrawer
      ref="drawerContainer"
      :type="'Access'"
      :drawer="openChatterModal.show"
      :typeValue="openChatterModal.userName"
      object-type="account_permission"
      :object-id="openChatterModal.accessId"
      @closeDrawer="closeUserChatterModal()"
      :chatterList="openChatterModal.chatterData"
      :totalCheckpointChatter="openChatterModal.totalUserChatter"
      :isEmployeeChatter="true"
      :avatar="openChatterModal.userAvatar"
      @requestMoreCheckpointsChatter="requestMoreUserChatter()"
    />
  </v-container>
</template>

<script>
import configService from "../../services/config";
import {
  buildAccountInvitesWebSocketUrl,
  isInvitePipelineStatus,
  mapInviteWsPayloadToAccessFields,
  unwrapInvitesWsPayload,
} from "@/utils/accountInvitesWebSocket";
import ExCreateEmployeeWizard from "@components/ExCreateEmployeeWizard.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import SettingsMenu from "@components/SettingsMenu/SettingsMenu.vue";
import HelpMenu from "@components/HelpMenu/HelpMenu.vue";
import DataTable from "@components/DataTable/DataTable.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import CancelInviteAdminUserPopup from "@components/AdminUserActions/CancelInviteAdminUserPopup.vue";
import {
  getListofUser,
  getListofUserV2,
  userResendInvite,
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
  resetPasswordForAccess,
  cancelPendingEmailChange,
  resetWelcomeWizardForAccess,
  manageAppAccess,
  getAppAccess,
  transferOwnerShip,
  cancelInviteUser,
  detachEmployeeAccess,
  getOperatingCurrency,
  getOperatingCountry,
  addOperatingCurrency,
  addOperatingCountry,
  updateOperatingCountry,
  partialUpdateOperatingCountry,
  retireOperatingCurrency,
  reactivateOperatingCurrency,
  setOperatingCurrencyAsReference,
  setOperatingCurrencyMode,
  setOperatingCurrencyRate,
  refreshOperatingCurrencyRate,
  bulkSetOperatingCurrencyMode,
  getListOfCurrencies,
  getCountryList,
  getChatterList,
} from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";
import {
  INVITE_FILTER_GROUP,
  NEEDS_ATTENTION_FILTER_LABEL,
  buildInviteRowMenuItems,
  buildPendingEmailChangeMenuItems,
  getInviteCellLines,
  getPendingEmailChangeLines,
  getInviteStageKey,
  getInviteTooltip,
  isInviteRow,
  needsAttention,
} from "@/utils/settingsUsersInvites";
import { escapeHtml } from "@components/JobFamilyConfirmDialog/jobFamilyConfirmDialog";
import {
  LD_FLAG_IDEA_MDP_506,
  LD_FLAG_IDEA_MDP_51,
  LD_FLAG_IDEA_MDP_646,
  LD_FLAG_IDEA_MDP_611,
  LD_FLAG_IDEA_MDP_621,
} from "@components/constants/launchDarklyFlagKeys";
import { getOperatingErrorMessage } from "@components/constants/operatingErrorCodes";
import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";
import IntegrationsAccessPanel from "./IntegrationsAccessPanel.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { requestAccessPermissionSync } from "@/utils/refreshAccessPermission";
import {
  mapOperatingCurrencyRows,
  resolveOperatingCurrencyStatus,
} from "@/utils/operatingCurrencyMapper";
import { patchAccessPermission } from "@/utils/accessPermissionPatch";
import { flagcdnFromFlagIcon } from "@components/utils/countryFlagIcon";
import UserActivity from "@components/SettingView/UserActivity.vue";
import UserCard from "@components/common/UserCard/UserCard.vue";
import UserPermissionBadge from "@components/UserPermissionBadge/UserPermissionBadge.vue";
import AccessStatusBadge from "@components/AccessStatusBadge/AccessStatusBadge.vue";
import UserStatusBadge from "@components/UserStatusBadge/UserStatusBadge.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import Icon from "@components/icons/Icon.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import AppIntegrationsList from "@components/AppIntegrationsList/AppIntegrationsList.vue";
import SlackIntegrationCard from "@components/SlackIntegrationCard/SlackIntegrationCard.vue";
import TeamsIntegrationCard from "@components/TeamsIntegrationCard/TeamsIntegrationCard.vue";
import RenameUserNameModal from "@components/RenameUserModal/RenameUserNameModal.vue";
import ChangeEmailModel from "@components/ChangeEmailModal/ChangeEmailModal.vue";
import { createNamespacedHelpers } from "vuex";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import EmptyState from "@components/EmptyState/EmptyState.vue";
import Button from "@components/buttons/Button.vue";
import clearStateMixin from "@components/views/mixins/clearStateMixin.js";
import ConfigurationView from "@components/SettingView/SettingConfiguration/ConfigurationView.vue";
import OwnerShipModal from "@components/OwnerShipModal/OwnerShipModal.vue";
import EditPermission from "@components/EditPermission/EditPermissionModal.vue";
import ChangeUserAccessDialog from "@components/ChangeUserAccessDialog/ChangeUserAccessDialog.vue";
import DetachEmployeeAccessDialog from "@components/DetachEmployeeAccessDialog/DetachEmployeeAccessDialog.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
const { mapActions } = createNamespacedHelpers("isLoading");
import { getAvailableApps } from "@/api";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
const NEEDS_ATTENTION_FETCH_LIMIT = 100;

const isRetryableUserListError = (error) => {
  const status = error?.response?.status;
  if (status) {
    return status >= 500;
  }
  return error?.code === "ERR_NETWORK" || error?.message === "Network Error";
};
export default {
  name: "SettingView",
  data() {
    const accessPermission = JSON.parse(localStorage.getItem("accessPermission"));
    const isOwner = accessPermission?.admin_permission === "owner";
    const isCoOwner = accessPermission?.admin_permission === "co-owner";
    let accessLevel = "";
    if (isOwner) {
      accessLevel = "owner";
    } else if (isCoOwner) {
      accessLevel = "co-owner";
    }

    return {
      payRanges: [
        { id: 1, name: "0-10K", code: "A" },
        { id: 2, name: "10K-20K", code: "B" },
        { id: 3, name: "20K-30K", code: "C" },
      ],
      selectedPayRange: null,
      isFlatCard: false,
      storeFilterKey: "organizationSettings",
      selectedTab: 0,
      isInitializing: true,
      dialog: false,
      email: "",
      accessLevel,
      baseHeaders: [
        {
          text: "User / Employee",
          sortable: true,
          value: "user",
          width: "100%",
        },
        {
          text: "Admin Permissions",
          sortable: false,
          value: "access",
        },
        {
          text: "Invite",
          sortable: false,
          value: "invite",
        },
        {
          text: "Last Seen",
          sortable: false,
          value: "lastlogin",
        },
        {
          text: "Access Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      accountID: "",
      filterText: "",
      userList: [],
      appAccessTypes: [
        {
          id: 1,
          app: {
            name: "Scale Readiness",
            status: "released",
            field: "has_readiness_access",
            icon: "sidebar_readiness",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Readiness",
        },
        {
          id: 2,
          app: {
            name: "Scale Knowledge",
            status: "released",
            field: "has_knowledge_access",
            icon: "sidebar_knowledge",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Knowledge",
        },
        {
          id: 3,
          app: {
            name: "Scale Performance",
            status: "released",
            field: "has_performance_access",
            icon: "sidebar_performance",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Performance",
        },
        {
          id: 4,
          app: {
            name: "Scale Compensation",
            status: "released",
            field: "has_compensation_access",
            icon: "sidebar_compensation",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Compensation",
        },
      ],
      paginationModel: {
        page: 1,
        total: 0,
        perPage: 10,
      },
      currentSelectedRow: {},
      dialogTitle1: false,
      appPermissions: [],
      appliedFilters: getDefaultFiltersForKey("organizationSettings"),
      clearSelection: "",
      showRenamePositionDialog: false,
      showCancelInviteUserPopup: false,
      showChangeEmailDialog: false,
      isAddDialogVisible: false,
      showUsersFilterModal: false,
      showDetachEmployeeAccessDialog: false,
      detachEmployeeAccessDialogFullName: "",
      detachEmployeeAccessDialogAvatar: {
        firstName: "",
        lastName: "",
        picture: "",
      },
      detachEmployeeAccessDialogRow: null,
      inviteLifecycleEnabled: false,
      usersFilterGroups: [
        {
          label: "Employee",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Is an Employee", value: "is_employee" },
            { label: "Is Not an Employee", value: "is_not_employee" },
          ],
        },
        {
          label: "Permissions",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "None", value: "none" },
            { label: "Administrator", value: "administrator" },
            { label: "Co-Owner", value: "co-owner" },
            { label: "Owner", value: "owner" },
          ],
        },
      ],
      usersStatusOptions: ["invited", "has access", "suspended", "blocked", "closed"],
      title: "Users",
      userActions: {
        showResendInvitePopup: false,
        showSuspendPopup: false,
        showGiveBackPopup: false,
        showResetPasswordPopup: false,
        showCancelEmailChangePopup: false,
      },
      showOnwerShipDialog: false,
      showChangeUserAccessDialog: false,
      /** Row.user.data from the table when opening Change User Access (avoids failed userList.find / stale selection). */
      changeUserAccessRow: null,
      items: [],
      availableApps: {},
      currencyList: [],
      currencyListTotal: 0,
      currencyListPage: 1,
      currencySearchQuery: "",
      /** Status chips sent to operating-currency list API (default matches table). */
      currencyStatusFilters: ["active"],
      currencyAddErrorCode: "",
      /** Rule 14 follow-up prompt after adding an operating country. mode: "add" | "reactivate". */
      currencyNudge: { show: false, mode: "add", currency: null, operatingCurrencyId: null },
      countryList: [],
      countryListTotal: 0,
      countryListPage: 1,
      countrySearchQuery: "",
      /** Status chips sent to operating-country list API (default matches table). */
      countryStatusFilters: ["active"],
      /** Bumps on each country-list fetch so stale responses do not overwrite newer ones. */
      countryListFetchGeneration: 0,
      dropdownCurrencyList: [],
      /** Catalog ids for currencies already in operating list (add-picker greyout). */
      operatingCatalogCurrencyIds: [],
      dropdownCountryList: [],
      /** Catalog ids for countries already in operating list (add-picker greyout). */
      operatingCatalogCountryIds: [],
      sortOptions: {
        field: null,
        direction: "asc",
      },
      openChatterModal: {
        show: false,
        chatterData: [],
        accessId: null,
        page: 1,
        totalUserChatter: 0,
        userName: "",
        userAvatar: {
          picture: "",
          firstName: "",
          lastName: "",
        },
      },
      isLoadingApps: false,
      usersSearchDebounceTimer: null,
      /** Bumps on each user-list fetch so stale responses do not hide the loader early. */
      userListFetchGeneration: 0,
      /** True when the latest Users list request failed; the table keeps its previous rows. */
      userListError: false,
      /** Resolved arguments of the failed Users list request, re-issued by Retry. */
      failedUserListRequest: null,
      transferOwnershipFetchGeneration: 0,
      transferOwnershipListOffset: 0,
      transferOwnershipHasMore: false,
      transferOwnershipLoadingMore: false,
      transferOwnershipSearchQuery: "",
      /** @type {WebSocket | null} */
      invitesRealtimeWs: null,
    };
  },
  mixins: [dropdownMixin, clearStateMixin],
  beforeDestroy() {
    if (this.usersSearchDebounceTimer) {
      clearTimeout(this.usersSearchDebounceTimer);
    }
    this.disconnectInvitesRealtimeSocket();
  },
  created() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));

    // If no view query param, we can show immediately
    if (!this.$route?.query?.view) {
      this.isInitializing = false;
    }

    this.resolveInviteLifecycleFlag();
    this.getUserList();
    this.applySavedFilters();
    this.$nextTick(() => {
      if (this.ideaMdp398AccountInvitesRealtimeEnabled && this.isUsersSettingsTabActive) {
        this.connectInvitesRealtimeSocket();
      }
    });
  },

  async mounted() {
    this.$loader.show();

    await this.$watchFlags({
      show_settingsConfigurationsTab: false,
      show_settingsIntegrationsTab: false,
      show_settingsSubscriptionsTab: false,
      "users-mdp-344": false,
      [LD_FLAG_IDEA_MDP_621]: false,
      [LD_FLAG_IDEA_MDP_51]: false,
      [LD_FLAG_IDEA_MDP_646]: false,
      [LD_FLAG_IDEA_MDP_506]: false,
    });

    if (
      this.$route?.query?.view ||
      (this.$route?.path || "").startsWith("/settings/configuration")
    ) {
      const configIndex = this.settingOptions.findIndex((opt) => opt.title === "Configurations");
      if (configIndex !== -1) {
        this.selectedTab = configIndex;
      }
    }

    await this.getApps();
    await this.getCurrencyList();
    await this.getOperatingCountry(1, undefined, { showLoader: false });
    // Catalog ids for add-picker greyout — not on the pagination hot path.
    await Promise.all([
      this.refreshOperatingCatalogCurrencyIds(),
      this.refreshOperatingCatalogCountryIds(),
    ]);
    // Currency add-picker uses CurrencyPicker scroll pagination (page size 10);
    // do not prefetch the full catalog into dropdownCurrencyList.
    await this.fetchDropdownCountryList();

    this.isInitializing = false;

    this.$loader.hide();
  },
  computed: {
    appUser() {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      return currentUser;
    },
    ideaMdp432AddUserWizardEnabled() {
      const store = this.$store;
      if (!store || !store.getters) return false;
      return store.getters["featureFlags/ideaMDP432"] === true;
    },
    ideaMdp570ResetPasswordEnabled() {
      const store = this.$store;
      if (!store || !store.getters) return false;
      return store.getters["featureFlags/ideaMDP570"] === true;
    },
    /** LD `idea-MDP-621`: "Reset Welcome wizard" row in the Settings → Users menu. */
    ideaMdp621ResetWelcomeWizardEnabled() {
      return this.flags?.[LD_FLAG_IDEA_MDP_621] === true;
    },
    /** LD `idea-MDP-51`: account-level Integrations status, Owner/Co-Owner-only actions, read-only for other admins. */
    ideaMdp51IntegrationsEnabled() {
      return this.flags?.[LD_FLAG_IDEA_MDP_51] === true;
    },
    /** LD `idea-MDP-646`: Microsoft Teams card on the Integrations tab (needs the `idea-MDP-51` status panel). */
    ideaMdp646TeamsEnabled() {
      return this.flags?.[LD_FLAG_IDEA_MDP_646] === true;
    },
    /** Non-admins never see the Integrations tab once `idea-MDP-51` is on. */
    canSeeIntegrationsTab() {
      if (!this.ideaMdp51IntegrationsEnabled) return true;
      try {
        const accessPermission = JSON.parse(localStorage.getItem("accessPermission"));
        return normalizeAdminPermission(accessPermission).hasElevatedNav;
      } catch (error) {
        return false;
      }
    },
    /** LD `idea-MDP-506`: Rule 14 currency prompt after adding an operating country. */
    ideaMdp506CurrencyNudgeEnabled() {
      return this.flags?.[LD_FLAG_IDEA_MDP_506] === true;
    },
    currencyNudgeHeadline() {
      return this.currencyNudge.mode === "reactivate"
        ? "Re-Activate Operating Currency"
        : "Add Operating Currency";
    },
    currencyNudgeInfo() {
      const name = this.currencyNudge.currency?.name || "this currency";
      return this.currencyNudge.mode === "reactivate"
        ? `Also reactivate ${name}?`
        : `Also add ${name} as an operating currency?`;
    },
    ideaMdp453NewGiveAccessToEmployee() {
      const store = this.$store;
      if (!store || !store.getters) return false;
      return store.getters["featureFlags/ideaMDP453"] === true;
    },
    /** LD `idea-MDP-398`: invite WebSocket + AccessStatusBadge pipeline labels on Settings → Users. */
    ideaMdp398AccountInvitesRealtimeEnabled() {
      const store = this.$store;
      if (!store || !store.getters) return false;
      return store.getters["featureFlags/ideaMDP398"] === true;
    },
    headers() {
      return this.baseHeaders.filter((header) => {
        if (header.value === "chatter") return !!this.flags["users-mdp-344"];
        if (header.value === "invite") return this.inviteLifecycleEnabled;
        return true;
      });
    },
    effectiveUsersFilterGroups() {
      return this.inviteLifecycleEnabled
        ? [...this.usersFilterGroups, INVITE_FILTER_GROUP]
        : this.usersFilterGroups;
    },
    needsAttentionFilterActive() {
      return (
        this.inviteLifecycleEnabled && this.appliedFilters.includes(NEEDS_ATTENTION_FILTER_LABEL)
      );
    },
    settingsUserV2Flags() {
      return !!this.$store.getters["featureFlags/ideaMDP449"];
    },
    usersSearchBarFiltersLabel() {
      return this.appliedFilters.length > 0 ? "More Filters" : "Filters";
    },
    settingOptions() {
      return [
        { title: "Users", count: this.paginationModel.total ?? 0 },
        ...(this.flags["show_settingsSubscriptionsTab"]
          ? [{ title: "Subscription", count: 0 }]
          : []),
        ...(this.flags["show_settingsConfigurationsTab"]
          ? [{ title: "Configurations", count: null }]
          : []),
        ...(this.flags["show_settingsIntegrationsTab"] && this.canSeeIntegrationsTab
          ? [{ title: "Integrations", count: null }]
          : []),
      ];
    },
    userAccountActionDialogObject() {
      if (this.userActions.showResendInvitePopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Resend Invite",
          description: `You are about to resend invite to ${firstName} ${lastName}`,
          actionText: "Resend",
          primary: true,
          show: true,
          termination: false,
        };
      } else if (this.userActions.showSuspendPopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Suspend Access",
          description: `You are about to suspend access for ${firstName} ${lastName}`,
          actionText: "Suspend",
          show: true,
          primary: false,
          termination: false,
          destructive: true,
        };
      } else if (this.userActions.showGiveBackPopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Give back Access",
          description: `You are about to give back access for ${firstName} ${lastName}`,
          actionText: "Give back Access",
          show: true,
          primary: true,
          termination: false,
          destructive: false,
        };
      } else if (this.userActions.showResetPasswordPopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Reset Password",
          description: `Send a password reset link to ${firstName} ${lastName}?`,
          actionText: "Send",
          show: true,
          primary: true,
          termination: false,
          destructive: false,
        };
      } else if (this.userActions.showCancelEmailChangePopup) {
        const pending = this.currentSelectedRow?.pending_email_change;
        const newEmail = escapeHtml(pending?.new_email || "");
        const currentEmail = escapeHtml(this.currentSelectedRow?.user?.email || "");
        return {
          title: "Cancel email change?",
          description: `The pending change to ${newEmail} will be cancelled. The Access stays on ${currentEmail} and the confirmation link stops working.`,
          actionText: "Cancel change",
          show: true,
          primary: false,
          termination: false,
          destructive: true,
        };
      }
      return {
        title: "",
        description: "",
        actionText: "",
        show: false,
        primary: false,
        termination: false,
      };
    },
    /** Prefer snapshot from the clicked table row so props always match the menu action. */
    changeUserAccessTarget() {
      return this.changeUserAccessRow || this.currentSelectedRow;
    },
    changeUserAccessDialogName() {
      const u = this.changeUserAccessTarget?.user;
      if (!u) return "";
      return `${u.first_name || ""} ${u.last_name || ""}`.trim();
    },
    changeUserAccessDialogAvatar() {
      return this.changeUserAccessTarget?.avatar || "";
    },
    changeUserAccessDialogUserLine() {
      return this.mapAdminPermissionToUserLine(this.changeUserAccessTarget?.admin_permission);
    },
    /**
     * Employee link id from the table row (row.user.data): prefer nested `employee.id`,
     * else `user.employee_id` when `employee` is null (API may only set one).
     */
    changeUserAccessDialogLinkedEmployee() {
      const row = this.changeUserAccessTarget;
      if (!row) return null;
      const fromEmployee = row.employee?.id;
      if (this.isPresentEmployeeLinkId(fromEmployee)) return fromEmployee;
      const fromUser = row.user?.employee_id;
      if (this.isPresentEmployeeLinkId(fromUser)) return fromUser;
      return null;
    },
    /** Employee id for DetachEmployeeAccessDialog (Users table row). */
    detachEmployeeAccessDialogEmployeeId() {
      const d = this.detachEmployeeAccessDialogRow?.user?.data;
      if (!d) return null;
      const fromEmployee = d.employee?.id;
      if (this.isPresentEmployeeLinkId(fromEmployee)) return fromEmployee;
      const fromUser = d.user?.employee_id;
      if (this.isPresentEmployeeLinkId(fromUser)) return fromUser;
      return null;
    },
    changeUserAccessDialogLoggedInRole() {
      try {
        const ap = JSON.parse(localStorage.getItem("accessPermission") || "{}")?.admin_permission;
        return ap === "co-owner" ? "Co-Owner" : "Owner";
      } catch {
        return "Owner";
      }
    },
    hasUsersActiveSearchOrFilters() {
      const t = this.filterText && String(this.filterText).trim().length > 0;
      return t || (this.appliedFilters && this.appliedFilters.length > 0);
    },
    showUsersFilterEmptyBanner() {
      return (
        !this.userListError &&
        this.filteredUsers.length === 0 &&
        this.paginationModel.total === 0 &&
        this.hasUsersActiveSearchOrFilters
      );
    },
    filteredUsers() {
      const rows = this.mapAccessRecordsToTableRows(this.userList);
      if (this.settingsUserV2Flags) {
        return rows;
      }
      return this.filterAndSortLegacyUserRows(rows);
    },
    usersSettingsTabIndex() {
      return this.settingOptions.findIndex((el) => el.title === "Users");
    },
    isUsersSettingsTabActive() {
      const i = this.usersSettingsTabIndex;
      return i !== -1 && this.selectedTab === i;
    },
  },
  watch: {
    filteredUsers: {
      handler(newVal) {
        if (this.settingsUserV2Flags) {
          return;
        }
        if (newVal) {
          this.paginationModel.total = newVal.length;
          this.paginationModel.page = 1;
        }
      },
    },
    filterText() {
      if (this.settingsUserV2Flags) {
        return;
      }
      this.paginationModel.page = 1;
    },
    appliedFilters() {
      if (this.settingsUserV2Flags) {
        return;
      }
      this.paginationModel.page = 1;
    },
    settingsUserV2Flags(newVal, oldVal) {
      if (oldVal === undefined) {
        return;
      }
      this.paginationModel.page = 1;
      this.getUserList();
    },
    isUsersSettingsTabActive: {
      handler(isActive) {
        if (!this.ideaMdp398AccountInvitesRealtimeEnabled) {
          return;
        }
        if (isActive) {
          this.connectInvitesRealtimeSocket();
        } else {
          this.disconnectInvitesRealtimeSocket();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(["setLoadingStatus"]),
    async getApps() {
      // Prevent duplicate simultaneous calls
      if (this.isLoadingApps) {
        return;
      }

      this.isLoadingApps = true;

      try {
        const accountId = localStorage.getItem("currentAccountID");
        const response = await getAvailableApps(accountId);
        localStorage.setItem("currentAccountDetails", JSON.stringify(response?.data));

        this.availableApps = response.data;
        this.appAccessTypes = [
          {
            id: 1,
            app: {
              name: "Scale Readiness",
              status: "released",
              field: "has_readiness_access",
              icon: "sidebar_readiness",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Readiness",
            isAppAvailable: this.availableApps.readiness,
          },
          {
            id: 2,
            app: {
              name: "Scale Knowledge",
              status: "released",
              field: "has_knowledge_access",
              icon: "sidebar_knowledge",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Knowledge",
            isAppAvailable: this.availableApps.knowledge,
          },
          {
            id: 3,
            app: {
              name: "Scale Performance",
              status: "released",
              field: "has_performance_access",
              icon: "sidebar_performance",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Performance",
            isAppAvailable: this.availableApps.performance,
          },
          {
            id: 4,
            app: {
              name: "Scale Compensation",
              status: "released",
              field: "has_compensation_access",
              icon: "sidebar_compensation",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Compensation",
            isAppAvailable: this.availableApps.compensation,
          },
        ];
      } catch (error) {
        console.error("[Settings] Error loading apps:", error);
      } finally {
        this.isLoadingApps = false;
      }
    },
    async emailUpdated() {
      this.formateData();
      this.getUserList();
      this.showChangeEmailDialog = false;
    },
    async cancelInviteAdminUser() {
      this.$loader.setAppLoading(true);
      const userId = this.currentSelectedRow?.user?.id;
      try {
        await cancelInviteUser(userId);
        this.showCancelInviteUserPopup = false;
        this.getUserList();
        this.showCancelInviteUserPopup = false;
      } catch (e) {
        console.error("error: ", e);
      }
      this.$loader.setAppLoading(false);
    },
    isPresentEmployeeLinkId(id) {
      if (id === null || id === undefined) return false;
      if (typeof id === "string" && id.trim() === "") return false;
      return true;
    },
    mapAdminPermissionToUserLine(adminPermission) {
      const p = (adminPermission ?? "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/-/g, "");
      if (p === "owner") return "Owner";
      if (p === "coowner" || p === "co-owner") return "Co-Owner";
      if (p === "admin") return "Administrative";
      return "None";
    },
    /** ChangeUserAccessDialog user_line_admin_access → manageAppAccess `admin_permission` */
    userLineToApiAdminPermission(userLine) {
      const map = {
        None: "none",
        Administrative: "admin",
        "Co-Owner": "co-owner",
        Owner: "owner",
      };
      return map[userLine] ?? "none";
    },
    onChangeUserAccessDialogInput(open) {
      this.showChangeUserAccessDialog = open;
      if (!open) this.changeUserAccessRow = null;
    },
    onChangeUserAccessDialogClose() {
      this.showChangeUserAccessDialog = false;
      this.changeUserAccessRow = null;
    },
    onSaveChangeUserAccess({ user_line_admin_access }) {
      const target = this.changeUserAccessRow || this.currentSelectedRow;
      if (!target?.user?.id) return;
      const admin_permission = this.userLineToApiAdminPermission(user_line_admin_access);
      this.$loader.setAppLoading(true);
      manageAppAccess(this.accountID, target.user.id, {
        admin_permission,
      })
        .then(() => {
          target.admin_permission = admin_permission;
          // Promoting to owner demotes the previous owner server-side; the local
          // row update alone leaves the old owner showing the "Owner" badge, so
          // refetch the full list to keep the single-owner constraint (ER-9743).
          if (admin_permission === "owner") {
            this.getUserList();
          } else {
            this.formateData();
          }
          this.showChangeUserAccessDialog = false;
          this.changeUserAccessRow = null;
        })
        .catch((err) => {
          console.error("onSaveChangeUserAccess:", err);
          const detail = err.response?.data?.detail;
          this.$notifications.error(
            typeof detail === "string" ? detail : "Something went wrong, please try again."
          );
        })
        .finally(() => {
          this.$loader.setAppLoading(false);
        });
    },
    getUserAccess(state, isSuspended) {
      if (state == "operational" && isSuspended === false) {
        return "has-access";
      } else if (isSuspended === true) {
        return "suspended";
      } else {
        return state;
      }
    },
    /**
     * Label string for AccessStatusBadge from an access-list row (matches invite WS + list API).
     */
    getAccessStatusBadgeStatusFromRow(accessRecord) {
      const u = accessRecord?.user;
      if (!u) return "Has Access";
      if (accessRecord.is_suspended === true) return "Suspended";
      const st = String(u.state || "").toLowerCase();
      if (st === "operational") return "Has Access";
      if (st === "blocked") return "Blocked";
      if (st === "closed") return "Closed";
      if (st === "invited") {
        const inv = String(u.invite_status || accessRecord.invite_status || "")
          .trim()
          .toLowerCase();
        if (isInvitePipelineStatus(inv)) {
          return inv.charAt(0).toUpperCase() + inv.slice(1);
        }
        return "Initiated";
      }
      return "Has Access";
    },
    /** Rule 7.2 — invite stage applies only when user.state is invited. */
    getUserStatusBadgeInviteStatusFromRow(accessRecord) {
      const st = String(accessRecord?.user?.state || "").toLowerCase();
      if (st !== "invited") return null;
      return accessRecord.user.invite_status || accessRecord.invite_status || null;
    },
    disconnectInvitesRealtimeSocket() {
      if (this.invitesRealtimeWs) {
        try {
          this.invitesRealtimeWs.close();
        } catch {
          /* ignore */
        }
        this.invitesRealtimeWs = null;
      }
    },
    connectInvitesRealtimeSocket() {
      this.disconnectInvitesRealtimeSocket();
      if (!this.ideaMdp398AccountInvitesRealtimeEnabled) {
        return;
      }
      if (typeof WebSocket === "undefined") {
        return;
      }
      if (!this.isUsersSettingsTabActive) {
        return;
      }
      const token = localStorage.getItem("token");
      if (!token || !this.accountID) {
        return;
      }
      let baseURL;
      try {
        baseURL = configService.get("baseURL");
      } catch {
        return;
      }
      const url = buildAccountInvitesWebSocketUrl(baseURL, this.accountID, token);
      if (!url) {
        return;
      }
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        console.warn("[invites ws] connect failed:", e);
        return;
      }
      this.invitesRealtimeWs = ws;
      ws.onmessage = (event) => this.onInvitesRealtimeMessage(event);
      ws.onerror = () => {
        /* browser logs details */
      };
    },
    onInvitesRealtimeMessage(event) {
      if (!this.ideaMdp398AccountInvitesRealtimeEnabled) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (payload == null || typeof payload !== "object") {
        return;
      }
      const effective = unwrapInvitesWsPayload(payload) || payload;
      const eventAccountId = effective.account_id ?? payload.account_id;
      if (
        eventAccountId != null &&
        String(eventAccountId).trim() !== "" &&
        String(eventAccountId) !== String(this.accountID)
      ) {
        return;
      }
      const mapped = mapInviteWsPayloadToAccessFields(effective);
      if (!mapped) {
        return;
      }
      const idx = this.findUserListIndexForInviteEntity(effective.entity_id);
      if (idx === -1) {
        return;
      }
      const rec = this.userList[idx];
      if (!rec.user || typeof rec.user !== "object") {
        return;
      }
      this.applyInviteRealtimeMappedFields(rec, mapped);
      // Always splice: Vue 2 `filteredUsers` + v-data-table may not re-run slots on deep `$set`
      // alone; re-inserting the row forces a refresh even when the payload repeats prior values.
      this.userList.splice(idx, 1, rec);
    },
    /** @returns {number} index in `userList` or -1 */
    findUserListIndexForInviteEntity(entityId) {
      if (entityId == null) {
        return -1;
      }
      const eid = String(entityId);
      return this.userList.findIndex((rec) => {
        if (!rec) return false;
        return (
          (rec.user != null && String(rec.user.id) === eid) ||
          (rec.id != null && String(rec.id) === eid)
        );
      });
    },
    /**
     * Applies WS-mapped fields when they differ (avoids redundant `$set`).
     * Caller must still `splice` the row so the table/badge refresh under Vue 2.
     */
    applyInviteRealtimeMappedFields(rec, mapped) {
      const u = rec.user;
      const inv = mapped.invite_status ?? "";
      if (
        u.state === mapped.state &&
        rec.is_suspended === mapped.is_suspended &&
        String(u.invite_status || "") === String(inv) &&
        String(rec.invite_status || "") === String(inv)
      ) {
        return;
      }
      this.$set(u, "state", mapped.state);
      this.$set(rec, "is_suspended", mapped.is_suspended);
      this.$set(u, "invite_status", inv);
      this.$set(rec, "invite_status", inv);
    },
    async resolveInviteLifecycleFlag() {
      this.inviteLifecycleEnabled = (await this.$getFlag(LD_FLAG_IDEA_MDP_611, false)) === true;
    },
    isInviteRow,
    getInviteCellLines,
    getInviteStageKey,
    getInviteTooltip,
    needsAttention,
    getUsersRowMenuItems(access) {
      const baseItems = this.getSettingsUsersItems(
        access,
        this.appUser,
        this.ideaMdp432AddUserWizardEnabled,
        this.ideaMdp570ResetPasswordEnabled,
        this.ideaMdp621ResetWelcomeWizardEnabled
      );
      if (!this.inviteLifecycleEnabled) return baseItems;
      const sessionPermission = JSON.parse(
        localStorage.getItem("accessPermission")
      )?.admin_permission;
      const requesterId = access?.pending_email_change?.requested_by?.access_id;
      const requesterPermission = this.userList.find(
        (x) => requesterId != null && String(x.id) === String(requesterId)
      )?.admin_permission;
      return buildPendingEmailChangeMenuItems(
        buildInviteRowMenuItems(baseItems, access, sessionPermission),
        access,
        sessionPermission,
        requesterPermission
      );
    },
    pendingEmailChangeLines(access) {
      const sessionPermission = JSON.parse(
        localStorage.getItem("accessPermission")
      )?.admin_permission;
      return getPendingEmailChangeLines(access, sessionPermission);
    },
    getUserStateLabel(state) {
      return state === "invited" ? "Invited on " : "Last seen on ";
    },
    mapAccessRecordsToTableRows(list) {
      const resultArray = [];
      if (!list || !Array.isArray(list)) {
        return resultArray;
      }
      return list.map((el) => ({
        user: {
          slot: "user",
          data: el,
        },
        access: {
          slot: "access",
        },
        ...(this.inviteLifecycleEnabled
          ? {
              invite: {
                slot: "invite",
              },
            }
          : {}),
        lastlogin: {
          slot: "lastlogin",
        },
        status: {
          slot: "status",
        },
        ...(this.flags["users-mdp-344"]
          ? {
              chatter: {
                slot: "chatter",
              },
            }
          : {}),
        actions: {
          slot: "actions",
          data: {},
        },
      }));
    },
    filterAndSortLegacyUserRows(resultArray) {
      let rows = resultArray;
      if (this.filterText && this.filterText.length > 0) {
        rows = rows.filter((el) => {
          const firstName = el.user.data.user.first_name.toUpperCase();
          const lastName = el.user.data.user.last_name.toUpperCase();
          const fullName = `${firstName} ${lastName}`;
          const filterTextUpper = this.filterText.toUpperCase();
          return fullName.includes(filterTextUpper);
        });
      }
      if (this.needsAttentionFilterActive) {
        rows = rows.filter((el) => needsAttention(el.user.data));
      }
      if (this.appliedFilters && this.appliedFilters.length > 0) {
        const userFilters = [
          "invited",
          "has access",
          "suspended",
          "blocked",
          "closed",
          "Is an Employee",
          "Is Not an Employee",
        ];
        const permissionFilters = [
          "Owner",
          "Co-Owner",
          "Administrator",
          "None",
          "Readiness",
          "Compensation",
          "Performance",
          "Knowledge",
        ];

        if (this.appliedFilters.some((val) => userFilters.includes(val))) {
          rows = rows.filter((el) => {
            return this.appliedFilters.some((el3) => {
              let val = false;
              if (el3 === "Is an Employee") {
                val = el.user.data.has_employee === true;
              } else if (el3 === "Is Not an Employee") {
                val = el.user.data.has_employee === false;
              } else if (el3 === "invited") {
                val = el.user.data.user.state === "invited" && el.user.data.is_suspended === false;
              } else if (el3 === "has access") {
                val =
                  el.user.data.user.state === "operational" && el.user.data.is_suspended === false;
              } else if (el3 === "suspended") {
                val = el.user.data.is_suspended === true;
              } else if (el3 === "blocked") {
                val = el.user.data.user.state === "blocked";
              } else if (el3 === "closed") {
                val = el.user.data.user.state === "closed";
              }
              return val;
            });
          });
        }

        const permissionFilter = this.appliedFilters.find((filter) =>
          permissionFilters.includes(filter)
        );
        if (permissionFilter) {
          rows = rows.filter((el) => {
            let val = false;
            if (permissionFilter === "Owner") {
              val = el.user.data.is_owner === true;
            } else if (permissionFilter === "Co-Owner") {
              val = el.user.data.is_co_owner === true;
            } else if (permissionFilter === "Administrator") {
              val = el.user.data.admin_permission === "admin";
            } else if (permissionFilter === "None") {
              val =
                !el.user.data.is_owner &&
                !el.user.data.is_co_owner &&
                el.user.data.admin_permission !== "admin";
            } else if (permissionFilter === "Readiness") {
              val = el.user.data.has_readiness_access === true;
            } else if (permissionFilter === "Compensation") {
              val = el.user.data.has_compensation_access === true;
            } else if (permissionFilter === "Performance") {
              val = el.user.data.has_performance_access === true;
            } else if (permissionFilter === "Knowledge") {
              val = el.user.data.has_knowledge_access === true;
            }
            return val;
          });
        }
      }

      if (this.sortOptions.field) {
        rows = [...rows].sort((a, b) => {
          let valueA;
          let valueB;

          if (this.sortOptions.field === "user") {
            valueA =
              a.user.data.user.first_name.toUpperCase() +
              " " +
              a.user.data.user.last_name.toUpperCase();
            valueB =
              b.user.data.user.first_name.toUpperCase() +
              " " +
              b.user.data.user.last_name.toUpperCase();
          } else {
            valueA = String(a.user.data.user[this.sortOptions.field] || "").toUpperCase();
            valueB = String(b.user.data.user[this.sortOptions.field] || "").toUpperCase();
          }

          if (this.sortOptions.direction === "asc") {
            if (valueA < valueB) return -1;
            if (valueA > valueB) return 1;
            return 0;
          }
          if (valueA > valueB) return -1;
          if (valueA < valueB) return 1;
          return 0;
        });
      } else {
        rows = [...rows].sort((a, b) => {
          const nameA =
            a.user.data.user.first_name.toUpperCase() +
            " " +
            a.user.data.user.last_name.toUpperCase();
          const nameB =
            b.user.data.user.first_name.toUpperCase() +
            " " +
            b.user.data.user.last_name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
      }
      return rows;
    },
    OnActionUserConfirmationDialog() {
      if (this.userActions.showResendInvitePopup) {
        this.resendInviteAction();
      } else if (this.userActions.showSuspendPopup) {
        this.suspendAccessAction();
      } else if (this.userActions.showGiveBackPopup) {
        this.giveBackAccessAction();
      } else if (this.userActions.showResetPasswordPopup) {
        this.resetPasswordAction();
      } else if (this.userActions.showCancelEmailChangePopup) {
        this.cancelEmailChangeAction();
      }
    },
    OnCloseUserConfirmationDialog() {
      if (this.userActions.showResendInvitePopup) {
        this.userActions.showResendInvitePopup = false;
      } else if (this.userActions.showSuspendPopup) {
        this.userActions.showSuspendPopup = false;
      } else if (this.userActions.showGiveBackPopup) {
        this.userActions.showGiveBackPopup = false;
      } else if (this.userActions.showResetPasswordPopup) {
        this.userActions.showResetPasswordPopup = false;
      } else if (this.userActions.showCancelEmailChangePopup) {
        this.userActions.showCancelEmailChangePopup = false;
      }
    },
    formatDate(date) {
      const originalDate = new Date(date);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[originalDate.getMonth()];
      const year = originalDate.getFullYear();
      const day = String(originalDate.getDate()).padStart(2, "0");

      // Pad hours and minutes with leading zeros
      const hours = String(originalDate.getHours()).padStart(2, "0");
      const minutes = String(originalDate.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
    formatLastLoginDate(date) {
      const originalDate = new Date(date);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[originalDate.getMonth()];
      const year = originalDate.getFullYear();
      const day = String(originalDate.getDate()).padStart(2, "0");

      // Use 24-hour format
      const hours = String(originalDate.getHours()).padStart(2, "0");
      const minutes = String(originalDate.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
    async onCreatedUser() {
      this.getUserList();
    },
    onUpdateShadow(val) {
      this.isFlatCard = val;
    },
    clearSettingsViewQueryParam() {
      if (!this.$route?.query?.view) {
        return;
      }
      const query = { ...this.$route.query };
      delete query.view;
      this.$router?.replace({ query }).catch(() => {});
    },
    handleTabChange(tabIndex) {
      // Normal tab change (re-clicking the already-active Configurations tab never
      // reaches here - HorizontalMenu only emits onSameTabClick for that case).
      this.isFlatCard = false;
      this.selectedTab = tabIndex;
      const configTabIndex = this.settingOptions.findIndex((opt) => opt.title === "Configurations");
      // Leaving the bookmarkable Configuration route for another tab must return
      // the URL to the plain /settings route, otherwise a refresh would land back
      // on the Configurations tab (ER-11887/ER-11888).
      if (
        (this.$route?.path || "").startsWith("/settings/configuration") &&
        tabIndex !== configTabIndex
      ) {
        this.$router?.replace("/settings").catch(() => {});
        return;
      }
      this.clearSettingsViewQueryParam();
    },
    // ER-11634: re-clicking the already-active Configurations tab must reset its
    // sub-page back to the hub view. The Configurations tab is guaranteed mounted
    // whenever this fires (it's already selected), so the reset is applied
    // synchronously - no $nextTick/setTimeout/$refs race.
    handleSameTabClick(tabIndex) {
      const configTabIndex = this.settingOptions.findIndex((opt) => opt.title === "Configurations");
      if (tabIndex !== configTabIndex) {
        return;
      }

      let configView = this.$refs.configurationView;
      // Handle case where ref is an array (when component is in v-for)
      if (Array.isArray(configView)) {
        configView =
          configView.find(
            (component) => component && component.$el && component.$el.offsetParent !== null
          ) || configView[0];
      }

      if (configView && typeof configView.onGoBack === "function") {
        configView.onGoBack();
      } else {
        this.clearSettingsViewQueryParam();
        this.isFlatCard = false;
      }
    },
    applySavedFilters() {
      if (this.settingsUserV2Flags) {
        return;
      }
      this.$store.dispatch("filterStore/loadFilters", this.storeFilterKey);
      const savedFilters = this.$store.state.filterStore.filters;
      if (savedFilters && savedFilters.length > 0) {
        this.appliedFilters = savedFilters;
      } else {
        this.appliedFilters = getDefaultFiltersForKey("organizationSettings");
      }
    },
    resendInviteAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        const obj = {
          user: this.currentSelectedRow?.user.id,
          account: this.accountID ?? this.currentSelectedRow.account,
        };
        userResendInvite(this.currentSelectedRow.user.id, obj)
          .then(() => {
            //
          })
          .catch((err) => {
            console.error("error", err);
          })
          .finally(() => {
            this.getUserList();
            this.setLoadingStatus(false);
            this.userActions.showResendInvitePopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showResendInvitePopup = false;
      }
    },
    suspendAccessAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        let obj = {
          email: this.currentSelectedRow.user.email,
          state: "suspended",
        };
        userStateUpdateSuspend(this.accountID, this.currentSelectedRow.id, obj)
          .then(async () => {
            this.currentSelectedRow.user.state = "suspended";
            this.formateData();
            this.getUserList();
            requestAccessPermissionSync(this.$root);
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$notifications.error(err.response.data.detail);
            } else {
              this.$notifications.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.setLoadingStatus(false);
            this.userActions.showSuspendPopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showSuspendPopup = false;
      }
    },
    giveBackAccessAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        let obj = {
          email: this.currentSelectedRow.user.email,
          state: "invited",
        };
        userStateUpdateGiveBack(this.accountID, this.currentSelectedRow.id, obj)
          .then(async () => {
            this.currentSelectedRow.user.state = "invited";
            this.formateData();
            this.getUserList();
            requestAccessPermissionSync(this.$root);
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$notifications.error(err.response.data.detail);
            } else {
              this.$notifications.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.setLoadingStatus(false);
            this.userActions.showGiveBackPopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showGiveBackPopup = false;
      }
    },
    buildAccessListApiFiltersFromUI() {
      const applied = this.appliedFilters || [];
      const filters = {};
      const access_status = [];
      for (const f of applied) {
        if (f === "invited") {
          access_status.push("invited");
        } else if (f === "has access") {
          access_status.push("has_access");
        } else if (f === "suspended") {
          access_status.push("suspended");
        } else if (f === "blocked") {
          access_status.push("blocked");
        } else if (f === "closed") {
          access_status.push("closed");
        } else if (f === NEEDS_ATTENTION_FILTER_LABEL && this.inviteLifecycleEnabled) {
          if (!access_status.includes("invited")) access_status.push("invited");
        } else if (f === "Is an Employee") {
          filters.employee = "is_employee";
        } else if (f === "Is Not an Employee") {
          filters.employee = "not_employee";
        } else if (f === "Owner") {
          filters.permission = "owner";
        } else if (f === "Co-Owner") {
          filters.permission = "co-owner";
        } else if (f === "Administrator") {
          filters.permission = "admin";
        } else if (f === "None") {
          filters.permission = "none";
        }
      }
      if (access_status.length) {
        filters.access_status = access_status;
      }
      return filters;
    },
    buildTransferOwnershipApiFiltersFromUI() {
      const f = this.buildAccessListApiFiltersFromUI();
      const rest = { ...f };
      delete rest.permission;
      rest.access_status = ["has_access"];
      return rest;
    },
    getOrderingParam() {
      if (this.sortOptions.field === "user") {
        return this.sortOptions.direction === "asc" ? "first_name" : "-first_name";
      }
      return "first_name";
    },
    async getUserListV2(overrides = {}) {
      const skipLoader = overrides.skipLoader === true;
      let fetchGen = 0;
      if (!skipLoader) {
        this.userListFetchGeneration += 1;
        fetchGen = this.userListFetchGeneration;
        this.$loader.show();
      }
      const limit = overrides.limit ?? this.paginationModel.perPage;
      const page = overrides.page ?? this.paginationModel.page;
      const offset = (page - 1) * limit;
      const search = overrides.search === undefined ? this.filterText : overrides.search;
      const filters = overrides.filters ?? this.buildAccessListApiFiltersFromUI();
      const ordering = overrides.ordering ?? this.getOrderingParam();
      const attentionOnly = this.needsAttentionFilterActive;
      try {
        const requestArgs = [
          this.accountID,
          attentionOnly ? NEEDS_ATTENTION_FETCH_LIMIT : limit,
          attentionOnly ? 0 : offset,
          search,
          filters,
          ordering,
        ];
        let res;
        try {
          res = await getListofUserV2(...requestArgs);
        } catch (firstError) {
          if (!isRetryableUserListError(firstError)) {
            throw firstError;
          }
          res = await getListofUserV2(...requestArgs);
        }
        let results = res.data?.results ?? [];
        let count = res.data?.count ?? 0;
        if (attentionOnly) {
          const attentionRows = results.filter(needsAttention);
          count = attentionRows.length;
          results = attentionRows.slice(offset, offset + limit);
        }
        this.userList = results;
        this.paginationModel = {
          page,
          total: count,
          perPage: limit,
        };
        this.formateData();
        this.userListError = false;
        this.failedUserListRequest = null;
      } catch (error) {
        console.error("getUserListV2:", error);
        // A newer request owns the table state; this stale failure must not flag it.
        if (skipLoader || fetchGen === this.userListFetchGeneration) {
          this.userListError = true;
          this.failedUserListRequest = {
            ...overrides,
            skipLoader: false,
            limit,
            page,
            search,
            filters,
            ordering,
          };
        }
      } finally {
        if (!skipLoader && fetchGen === this.userListFetchGeneration) {
          this.$loader.hide();
        }
      }
    },
    retryUserList() {
      return this.getUserListV2(this.failedUserListRequest ?? {});
    },
    getUserList(overrides) {
      if (this.settingsUserV2Flags) {
        return this.getUserListV2(overrides);
      }
      return this.getUserListLegacy();
    },
    getUserListLegacy() {
      return getListofUser(this.accountID)
        .then((res) => {
          this.userList = res.data.results;
          this.formateData();
          if (res.data && res.data.count) {
            this.paginationModel.total = res.data.count;
          }
        })
        .catch((error) => {
          this.userList = [];
          console.log("error: ", error);
        });
    },
    async requestData(data) {
      if (data && data.page) {
        if (this.settingsUserV2Flags) {
          await this.getUserListV2({ page: data.page });
        } else {
          this.paginationModel.page = data.page;
        }
      }
    },
    async loadUsers() {
      if (this.settingsUserV2Flags) {
        await this.getUserListV2({ page: this.paginationModel.page });
      } else {
        await this.getUserListLegacy();
      }
    },
    transferOwnershipRowToPickerItem(element, { apiFiltered = false } = {}) {
      const user = element?.user;
      if (!user) return null;
      const adminPermRaw =
        element?.admin_permission ??
        element?.user?.data?.admin_permission ??
        element?.user?.admin_permission;
      const adminPerm =
        adminPermRaw != null && String(adminPermRaw).trim() !== ""
          ? String(adminPermRaw).toLowerCase().trim()
          : "";
      if (adminPerm === "owner") return null;
      if (!adminPerm && element.is_owner === true) return null;
      if (!apiFiltered) {
        if (user.state === "closed" || user.state === "blocked") return null;
        if (element.is_suspended === true) return null;
      }
      const fullName = user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
      return {
        id: user.id,
        fullName: fullName || user.email || "",
        email: user.email,
      };
    },
    onTransferOwnershipModalBack() {
      this.showOnwerShipDialog = false;
    },
    loadMoreTransferOwnershipCandidates() {
      return this.loadTransferOwnershipCandidateItems("", { append: true });
    },
    resetTransferOwnershipCandidateSearch(search) {
      this.transferOwnershipFetchGeneration += 1;
      this.transferOwnershipSearchQuery = search;
      this.transferOwnershipListOffset = 0;
      this.transferOwnershipHasMore = false;
      this.transferOwnershipLoadingMore = false;
      this.items = [];
    },
    transferOwnershipAppendPrecheckOK() {
      return (
        this.settingsUserV2Flags &&
        this.transferOwnershipHasMore &&
        !this.transferOwnershipLoadingMore
      );
    },
    async fetchTransferOwnershipCandidatesV2(append, search, gen) {
      if (append) {
        this.transferOwnershipLoadingMore = true;
      }
      const limit = 10;
      const offset = append ? this.transferOwnershipListOffset : 0;
      const filters = this.buildTransferOwnershipApiFiltersFromUI();
      const ordering = this.getOrderingParam();
      const { data } = await getListofUserV2(
        this.accountID,
        limit,
        offset,
        search,
        filters,
        ordering
      );
      if (gen !== this.transferOwnershipFetchGeneration) return;
      const rows = data?.results ?? [];
      const count = data?.count ?? 0;
      if (rows.length === 0) {
        this.transferOwnershipHasMore = false;
      } else {
        this.transferOwnershipListOffset = offset + rows.length;
        this.transferOwnershipHasMore = this.transferOwnershipListOffset < count;
      }
      const seenIds = append ? new Set(this.items.map((i) => i.id)) : new Set();
      const batchItems = [];
      for (const element of rows) {
        const item = this.transferOwnershipRowToPickerItem(element, {
          apiFiltered: true,
        });
        if (item != null && !seenIds.has(item.id)) {
          seenIds.add(item.id);
          batchItems.push(item);
        }
      }
      if (append) {
        this.items = [...this.items, ...batchItems];
      } else {
        this.items = batchItems;
      }
    },
    async fetchTransferOwnershipCandidatesLegacy(search, gen) {
      const { data } = await getListofUser(this.accountID, 1, 500);
      if (gen !== this.transferOwnershipFetchGeneration) return;
      const list = data?.results || [];
      const mapped = [];
      list.forEach((element) => {
        const item = this.transferOwnershipRowToPickerItem(element, {
          apiFiltered: false,
        });
        if (item) mapped.push(item);
      });
      const q = search.toLowerCase();
      this.items = q
        ? mapped.filter(
            (item) =>
              item.fullName.toLowerCase().includes(q) ||
              String(item.email || "")
                .toLowerCase()
                .includes(q) ||
              String(item.id).includes(q)
          )
        : mapped;
      this.transferOwnershipHasMore = false;
    },
    async loadTransferOwnershipCandidateItems(searchQuery = "", options = {}) {
      const append = options.append === true;
      const search = append ? this.transferOwnershipSearchQuery : String(searchQuery ?? "").trim();

      if (append) {
        if (!this.transferOwnershipAppendPrecheckOK()) return;
      } else {
        this.resetTransferOwnershipCandidateSearch(search);
      }

      const gen = this.transferOwnershipFetchGeneration;

      try {
        if (this.settingsUserV2Flags) {
          await this.fetchTransferOwnershipCandidatesV2(append, search, gen);
        } else {
          await this.fetchTransferOwnershipCandidatesLegacy(search, gen);
        }
      } catch (e) {
        if (gen !== this.transferOwnershipFetchGeneration) return;
        console.error("loadTransferOwnershipCandidateItems:", e);
        this.items = [];
        this.transferOwnershipHasMore = false;
      } finally {
        if (append) {
          this.transferOwnershipLoadingMore = false;
        }
      }
    },
    /**
     * Human-readable label for `admin_permission` from the API (access column).
     */
    displayAdminPermission(raw) {
      const ap = typeof raw === "string" ? raw.toLowerCase().trim() : "";
      if (ap === "owner") return "Owner";
      if (ap === "co-owner") return "Co-Owner";
      if (ap === "admin" || ap === "administrator") return "Admin";
      if (ap === "none" || ap === "") return "";
      if (raw == null || raw === "") return "";
      return String(raw)
        .replace(/[-_]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    },
    formateData() {
      this.userList.forEach((element) => {
        element.user.full_name = element.user.first_name + " " + element.user.last_name;
        element.actionMenu = [];
        if (element.user.state == "invited") {
          element.actionMenu.push({
            name: "Resend Invite",
            value: "resend_invite",
            icon: "icons/dropdown/resend.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "draft") {
          element.actionMenu.push({
            name: "Activate",
            value: "activate",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "active") {
          element.actionMenu.push({
            name: "Suspend",
            value: "suspend",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "suspended") {
          element.actionMenu.push({
            name: "Re-Activate",
            value: "reactivate",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        }
        if (!element.is_owner && !element.is_co_owner && !element.is_suspended) {
          element.actionMenu.push({
            name: "Manage Apps Access",
            value: "manage_apps",
            icon: "edit_employee.svg",
            order: 1,
            disabled: false,
          });
        }
        if (!element.is_owner && !element.is_suspended) {
          element.actionMenu.push({
            name: "Suspend",
            value: "suspend",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
            negative: true,
          });
        }
        if (!element.is_owner && element.is_suspended) {
          element.actionMenu.push({
            name: "Give back access",
            value: "give_back_access",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        }
      });
    },
    resolveUserListMatchForRow(tableRowData) {
      if (tableRowData === null || tableRowData === undefined) {
        return null;
      }
      return this.userList.find((x) => String(x.id) === String(tableRowData.id));
    },
    handleUsersTableDropdownAction(value, row, tableRowData) {
      switch (value) {
        case "resend_invite":
          this.userActions.showResendInvitePopup = true;
          break;
        case "suspend":
          this.userActions.showSuspendPopup = true;
          break;
        case "give_back_access":
          this.userActions.showGiveBackPopup = true;
          break;
        case "change_admin_permission_action":
          this.changeUserAccessRow = tableRowData || null;
          this.showChangeUserAccessDialog = true;
          break;
        case "change_permissions_action":
          this.appPermissions = [];
          this.dialogTitle1 = true;
          this.appAccessTypes.forEach((access) => {
            if (row.user.data[access.app.field]) {
              this.appPermissions.push(access.id);
            }
          });
          break;
        case "rename_action":
          this.showRenamePositionDialog = true;
          break;
        case "change_email_action":
          this.showChangeEmailDialog = true;
          break;
        case "cancel_email_change_action":
          this.userActions.showCancelEmailChangePopup = true;
          break;
        case "transfer_ownership_action":
          this.showOnwerShipDialog = true;
          this.loadTransferOwnershipCandidateItems();
          break;
        case "cancel_invite_action":
          this.showCancelInviteUserPopup = true;
          break;
        case "reset_password_action":
          this.userActions.showResetPasswordPopup = true;
          break;
        case "reset_welcome_wizard_action":
          this.resetWelcomeWizardAction();
          break;
        default:
          break;
      }
    },
    handleDropdownClick(data, row) {
      const tableRowData = row?.user?.data;
      const matched = this.resolveUserListMatchForRow(tableRowData);
      this.currentSelectedRow = matched ?? tableRowData ?? null;
      this.changeUserAccessRow = null;
      this.handleUsersTableDropdownAction(data.value, row, tableRowData);
    },
    userCardEmployeeStatus(row) {
      const data = row?.user?.data;
      if (!data) {
        return "";
      }
      if (data.employee) {
        return data.employee?.current_status ?? "";
      }
      return data.user?.state_str === "Operational" ? "active" : "invited";
    },
    transferOwner(event) {
      this.setLoadingStatus(true);
      const obj = {
        email: event.user.email,
      };
      transferOwnerShip(this.accountID, obj)
        .then(async () => {
          // The logged-in owner is demoted to co-owner by this transfer, so
          // refresh their own access record (mirrors the login flow) to keep
          // localStorage `accessPermission` in sync instead of staying "owner".
          await this.refreshLoggedInUserAccess();
          this.loadUsers();
        })
        .catch((err) => {
          console.error("error", err);
        })
        .finally(() => {
          this.setLoadingStatus(false);
          this.showOnwerShipDialog = false;
        });
    },
    async refreshLoggedInUserAccess() {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.id) return;
      try {
        const response = await getAppAccess(this.accountID, user.id);
        const patchUserAccessEnabled = await this.$getFlag("patch-user-access", false);
        const patchedAccessPermission = patchAccessPermission({
          accessPermission: response.data,
          accountId: this.accountID,
          user,
          isEnabled: patchUserAccessEnabled,
        });
        localStorage.setItem("accessPermission", JSON.stringify(patchedAccessPermission));
        this.$root.$emit("accessPermissionUpdated", patchedAccessPermission);
        const adminPermission = patchedAccessPermission?.admin_permission;
        this.accessLevel =
          adminPermission === "owner" || adminPermission === "co-owner" ? adminPermission : "";
      } catch (err) {
        console.error("Failed to refresh access permission after ownership transfer", err);
      }
    },
    resetPasswordAction() {
      this.setLoadingStatus(true);
      const accessId = this.currentSelectedRow?.id;
      if (accessId) {
        resetPasswordForAccess(this.accountID, accessId)
          .then(() => {
            this.$notifications.success("Password reset link has been sent");
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$notifications.error(err.response.data.detail);
            } else {
              this.$notifications.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.setLoadingStatus(false);
            this.userActions.showResetPasswordPopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showResetPasswordPopup = false;
      }
    },
    cancelEmailChangeAction() {
      const accessId = this.currentSelectedRow?.id;
      if (!accessId) {
        this.userActions.showCancelEmailChangePopup = false;
        return;
      }
      this.setLoadingStatus(true);
      cancelPendingEmailChange(this.accountID, accessId)
        .then(() => {
          this.$notifications.success("Pending email change cancelled");
        })
        .catch((err) => {
          this.$notifications.error(
            err.response?.data?.detail || "something went wrong, please try again."
          );
        })
        .finally(() => {
          this.setLoadingStatus(false);
          this.userActions.showCancelEmailChangePopup = false;
          this.getUserList();
        });
    },
    resetWelcomeWizardAction() {
      const row = this.currentSelectedRow;
      const accessId = row?.id;
      if (!accessId) return;
      const name = [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ");
      this.setLoadingStatus(true);
      resetWelcomeWizardForAccess(this.accountID, accessId)
        .then(() => {
          this.$notifications.success(`Welcome wizard reset for ${name}.`);
        })
        .catch((err) => {
          if (err.response?.data?.detail) {
            this.$notifications.error(err.response.data.detail);
          } else {
            this.$notifications.error("something went wrong, please try again.");
          }
        })
        .finally(() => {
          this.setLoadingStatus(false);
        });
    },
    updateAppPermissions(val) {
      this.appPermissions = val;
    },
    onSaveAppAccess() {
      this.$loader.setAppLoading(true);
      const obj = {};
      this.appAccessTypes.forEach((item) => {
        obj[item.app.field] = this.appPermissions.includes(item.id);
      });
      manageAppAccess(this.accountID, this.currentSelectedRow.user.id, obj)
        .then(() => {
          this.appAccessTypes.forEach((access) => {
            this.currentSelectedRow[access.app.field] = this.appPermissions.includes(access.id);
          });
          this.formateData();
        })
        .catch((err) => {
          console.log("err: ", err);
          this.$notifications.error("something went wrong, please try again.");
        })
        .finally(() => {
          this.dialogTitle1 = false;
          this.$loader.setAppLoading(false);
        });
    },
    onUsersSearchBarClick(type) {
      if (type === "add") {
        this.isAddDialogVisible = true;
      } else if (type === "filter") {
        this.showUsersFilterModal = true;
      }
    },
    onUsersSearchBarKeydown(event) {
      const target = event && event.target;
      if (!target) {
        return;
      }
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
        return;
      }
    },
    onUsersFilterModalApply(filters) {
      this.filteredModalUsers(filters);
      this.showUsersFilterModal = false;
    },
    onFilter(data) {
      this.filterText = data;
      this.paginationModel.page = 1;
      if (this.usersSearchDebounceTimer) {
        clearTimeout(this.usersSearchDebounceTimer);
      }
      if (this.settingsUserV2Flags) {
        this.usersSearchDebounceTimer = setTimeout(() => {
          this.usersSearchDebounceTimer = null;
          this.getUserListV2({ page: 1 });
        }, 400);
      }
      const configTabIndex = this.settingOptions.findIndex((opt) => opt.title === "Configurations");
      if (configTabIndex !== -1 && this.selectedTab === configTabIndex) {
        this.$emit("onFilterApplied", data);
      }
    },
    removeFilter(removedFilter) {
      this.$store.dispatch("filterStore/updateFilters", {
        key: this.storeFilterKey,
        filters: this.appliedFilters,
      });
      this.clearSelection = removedFilter;
      this.paginationModel.page = 1;
      this.$nextTick(() => {
        if (this.settingsUserV2Flags) {
          this.getUserListV2({ page: 1 });
        }
      });
    },
    filteredModalUsers(filters) {
      const newArray = filters
        .map((item) => {
          if (item.value !== null) {
            if (typeof item === "string") {
              return item;
            } else if (
              typeof item === "object" &&
              // eslint-disable-next-line no-prototype-builtins
              item.hasOwnProperty("value")
            ) {
              return item.value;
            }
          }
        })
        .filter(Boolean);
      this.appliedFilters = newArray;
      // Reset pagination to first page when filters are applied
      this.paginationModel.page = 1;
      this.$store.dispatch("filterStore/updateFilters", {
        key: this.storeFilterKey,
        filters: this.appliedFilters,
      });
      if (this.settingsUserV2Flags) {
        this.getUserListV2({ page: 1 });
      }
    },
    setUserStatus(user) {
      if (user.state == "") {
        return "has-access";
      } else {
        return "has-access";
      }
    },
    clearSearchFilter() {
      if (this.usersSearchDebounceTimer) {
        clearTimeout(this.usersSearchDebounceTimer);
        this.usersSearchDebounceTimer = null;
      }
      const hadFilters = this.appliedFilters?.length > 0;
      const hadText = this.filterText && this.filterText.length > 0;
      this.filterText = "";
      if (hadFilters) {
        this.filteredModalUsers([]);
      } else if (hadText) {
        this.paginationModel.page = 1;
        if (this.settingsUserV2Flags) {
          this.getUserListV2({ page: 1 });
        }
      }
    },
    toggleAppSelect(appId) {
      if (this.appPermissions.includes(appId)) {
        this.appPermissions = [
          ...this.appPermissions.filter((appInstallId) => appInstallId != appId),
        ];
      } else {
        this.appPermissions = [...this.selectedApps, appId];
      }
    },
    onPayRangeSelected(selectedItem) {
      this.selectedPayRange = selectedItem;
      console.log("Selected Pay Range:", selectedItem);
    },
    onPayRangeCleared() {
      this.selectedPayRange = null;
      console.log("Pay range selection cleared");
    },
    async drowdownCurrencyList() {
      // Match fetchDropdownCountryList: API page size is capped (requesting
      // 200 still returns ≤100), so stop only when count is exhausted — not
      // when the first page is shorter than an oversized batch (QA: Add
      // Currency modal must show the full catalog).
      this.dropdownCurrencyList = [];
      const pageSize = 100;
      let allResults = [];
      let page = 1;
      let total = Infinity;
      const maxPages = 40;
      try {
        while (allResults.length < total && page <= maxPages) {
          try {
            const res = await getListOfCurrencies({
              page,
              size: pageSize,
              limit: pageSize,
              offset: (page - 1) * pageSize,
            });
            const results = res?.data?.results ?? [];
            const reportedTotal = typeof res?.data?.count === "number" ? res.data.count : null;
            if (typeof reportedTotal === "number" && reportedTotal >= 0) {
              total = reportedTotal;
            }
            if (!results.length) {
              break;
            }
            allResults = [...allResults, ...results];
            if (results.length < pageSize) {
              break;
            }
            page += 1;
          } catch (batchError) {
            break;
          }
        }

        const mapped = [];
        const seenIds = new Set();
        allResults.forEach((element) => {
          // Catalog for add-picker: only Currency.active = true (AC).
          if (!element || element.active === false) {
            return;
          }
          const cid = element.id;
          if (cid == null || cid === "" || seenIds.has(String(cid))) {
            return;
          }
          seenIds.add(String(cid));
          const firstFlag = element.countries?.[0]?.flag_icon;
          mapped.push({
            id: element.id,
            name: element.name,
            code: element.code,
            symbol: element.symbol,
            active: element.active !== false,
            countries: element.countries
              ?.map((country) => ({
                ...country,
                flag_icon: country.flag_icon
                  ? `${configService.get("baseURL")}${country.flag_icon}`
                  : null,
              }))
              .sort((a, b) => b.name.localeCompare(a.name)),
            icon: firstFlag ? `${firstFlag}` : "",
          });
        });
        this.dropdownCurrencyList = mapped;
      } catch (error) {
        this.dropdownCurrencyList = [];
        console.log("error: ", error);
      }
    },
    extractCountryListRows(response) {
      let payload;
      if (response === null || response === undefined) {
        payload = response;
      } else {
        const d = response.data;
        if (d !== null && d !== undefined) {
          payload = d;
        } else {
          payload = response;
        }
      }
      if (payload === null || payload === undefined) {
        return { rows: [], total: 0 };
      }
      if (Array.isArray(payload)) {
        return { rows: payload, total: payload.length };
      }
      let rows = [];
      if (Array.isArray(payload.results)) {
        rows = payload.results;
      } else if (Array.isArray(payload.data)) {
        rows = payload.data;
      } else if (payload.data && Array.isArray(payload.data.results)) {
        rows = payload.data.results;
      }
      let total = rows.length;
      if (typeof payload.count === "number") {
        total = payload.count;
      } else if (typeof payload.total === "number") {
        total = payload.total;
      }
      return { rows, total };
    },
    async fetchDropdownCountryList() {
      try {
        // API caps page size at 100; requesting more still returns only 100.
        const pageSize = 100;
        let allResults = [];
        let page = 1;
        let total = Infinity;
        const maxPages = 40;

        while (allResults.length < total && page <= maxPages) {
          try {
            const res = await getCountryList({
              page,
              size: pageSize,
              limit: pageSize,
              offset: (page - 1) * pageSize,
            });
            const { rows, total: reportedTotal } = this.extractCountryListRows(res);
            if (typeof reportedTotal === "number" && reportedTotal >= 0) {
              total = reportedTotal;
            }
            if (!rows.length) {
              break;
            }
            allResults = [...allResults, ...rows];
            if (rows.length < pageSize) {
              break;
            }
            page += 1;
          } catch (batchError) {
            console.warn(`Failed to fetch country page ${page}:`, batchError);
            break;
          }
        }

        const uniqueResults = [];
        const seenIds = new Set();
        allResults.forEach((country) => {
          const cid = country === null || country === undefined ? null : country.id ?? country.pk;
          if (
            cid !== null &&
            cid !== undefined &&
            cid !== "" &&
            seenIds.has(String(cid)) === false
          ) {
            uniqueResults.push({ ...country, id: cid });
            seenIds.add(String(cid));
          }
        });

        this.dropdownCountryList = uniqueResults.map((country, index) => {
          if (country.id == null) {
            console.warn(`Country at index ${index} is missing id property:`, country);
          }
          return {
            id: country.id,
            name: country.name,
            currency: country.currency ?? null,
            icon:
              flagcdnFromFlagIcon(country.flag_icon) ||
              (country.flag_icon ? `${configService.get("baseURL")}${country.flag_icon}` : null),
          };
        });
      } catch (error) {
        this.dropdownCountryList = [];
        console.error("error: ", error);
      }
    },
    catalogCurrencyIdFromAddCurrencyEvent(event) {
      if (event === null || event === undefined) {
        return null;
      }
      const raw = event.id ?? event.currency_id;
      if (raw === null || raw === undefined || raw === "") {
        return null;
      }
      return raw;
    },
    catalogCurrencyIdFromOperatingRow(row) {
      if (row == null || typeof row !== "object") return null;
      if (row.currency_id != null) return row.currency_id;
      if (row.currencyId != null) return row.currencyId;
      const c = row.currency;
      if (c != null && typeof c === "object" && c.id != null) return c.id;
      if (typeof c === "number" || typeof c === "string") return c;
      return null;
    },
    rowsBatchSignature(rows) {
      return rows.map((r) => (r && r.id != null ? String(r.id) : "")).join("|");
    },
    addOperatingCatalogIds(rows, extractId, ids) {
      rows.forEach((row) => {
        const raw = extractId(row);
        const key = raw == null ? "" : String(raw).trim();
        if (key) {
          ids.add(key);
        }
      });
    },
    operatingCatalogPagingDone(rows, offset, reportedTotal, repeated) {
      if (repeated || rows.length === 0) {
        return true;
      }
      return reportedTotal != null && offset >= reportedTotal;
    },
    /**
     * Walks every page of an operating-list endpoint and returns the catalog
     * ids of already-added rows. Shared by the currency and country add-pickers
     * so an item is greyed out regardless of which table page is loaded.
     */
    async collectOperatingCatalogIds(fetcher, extractId) {
      if (!this.accountID) {
        return [];
      }
      const pageSize = 100;
      const maxPages = 500;
      const ids = new Set();
      let offset = 0;
      let reportedTotal = null;
      let previousSignature = null;
      for (let page = 0; page < maxPages; page += 1) {
        const res = await fetcher(this.accountID, {
          offset,
          size: pageSize,
          search: "",
        });
        const rows = res?.data?.results ?? [];
        const count = res?.data?.count;
        if (typeof count === "number" && count >= 0) {
          reportedTotal = count;
        }
        const signature = this.rowsBatchSignature(rows);
        const repeated = offset > 0 && signature !== "" && signature === previousSignature;
        previousSignature = signature;
        this.addOperatingCatalogIds(rows, extractId, ids);
        offset += rows.length;
        if (this.operatingCatalogPagingDone(rows, offset, reportedTotal, repeated)) {
          break;
        }
      }
      return Array.from(ids);
    },
    async refreshOperatingCatalogCurrencyIds() {
      try {
        this.operatingCatalogCurrencyIds = await this.collectOperatingCatalogIds(
          getOperatingCurrency,
          (row) => this.catalogCurrencyIdFromOperatingRow(row)
        );
      } catch (e) {
        console.error("refreshOperatingCatalogCurrencyIds:", e);
        this.operatingCatalogCurrencyIds = [];
      }
    },
    async getCurrencyList(page, explicitSearch) {
      const size = 10;
      let targetPage = this.currencyListPage;
      if (typeof page === "number" && Number.isNaN(page) === false) {
        targetPage = page;
      }
      const offset = (targetPage - 1) * size;
      let searchParam = "";
      if (explicitSearch !== undefined && explicitSearch !== null) {
        searchParam = String(explicitSearch).trim();
      } else if (typeof this.currencySearchQuery === "string") {
        searchParam = this.currencySearchQuery.trim();
      }
      const statusParam = Array.isArray(this.currencyStatusFilters)
        ? this.currencyStatusFilters.filter((s) => s === "active" || s === "retired")
        : [];
      try {
        const res = await getOperatingCurrency(this.accountID, {
          offset,
          size,
          search: searchParam,
          status: statusParam,
        });
        this.currencyListPage = targetPage;
        this.currencyListTotal = 0;
        if (typeof res.data.count === "number") {
          this.currencyListTotal = res.data.count;
        }
        this.currencyList = mapOperatingCurrencyRows(
          res.data.results ?? [],
          configService.get("baseURL")
        );
      } catch (error) {
        this.currencyList = [];
        this.currencyListTotal = 0;
        this.operatingCatalogCurrencyIds = [];
        console.error("Error fetching currency list:", error);
      } finally {
        // this.setLoadingStatus(false);
      }
    },
    onCurrencySearch(query) {
      let q = "";
      if (typeof query === "string") {
        q = query.trim();
      } else if (query !== null && query !== undefined) {
        q = String(query).trim();
      }
      this.currencySearchQuery = q;
      this.currencyListPage = 1;
      return this.getCurrencyList(1);
    },
    onCurrencyStatusFilter(filters) {
      const next = Array.isArray(filters)
        ? filters.filter((s) => typeof s === "string" && s.trim())
        : [];
      this.currencyStatusFilters = next;
      this.currencyListPage = 1;
      return this.getCurrencyList(1);
    },
    catalogCountryIdFromOperatingRow(row) {
      if (row == null || typeof row !== "object") return null;
      const c = row.country;
      if (c != null && typeof c === "object" && c.id != null) return c.id;
      if (typeof c === "number" || typeof c === "string") return c;
      if (row.country_id != null) return row.country_id;
      return null;
    },
    async refreshOperatingCatalogCountryIds() {
      try {
        this.operatingCatalogCountryIds = await this.collectOperatingCatalogIds(
          getOperatingCountry,
          (row) => this.catalogCountryIdFromOperatingRow(row)
        );
      } catch (e) {
        console.error("refreshOperatingCatalogCountryIds:", e);
        this.operatingCatalogCountryIds = [];
      }
    },
    async getOperatingCountry(page, explicitSearch, { showLoader = false } = {}) {
      const size = 10;
      let targetPage = this.countryListPage;
      if (typeof page === "number" && Number.isNaN(page) === false) {
        targetPage = page;
      }
      const offset = (targetPage - 1) * size;
      let searchParam = "";
      if (explicitSearch !== undefined && explicitSearch !== null) {
        searchParam = String(explicitSearch).trim();
      } else if (typeof this.countrySearchQuery === "string") {
        searchParam = this.countrySearchQuery.trim();
      }
      const statusParam = Array.isArray(this.countryStatusFilters)
        ? this.countryStatusFilters.filter((s) => typeof s === "string" && s.trim())
        : [];
      this.countryListFetchGeneration += 1;
      const generation = this.countryListFetchGeneration;
      if (showLoader) {
        this.$loader.show();
      }
      try {
        const res = await getOperatingCountry(this.accountID, {
          offset,
          size,
          search: searchParam,
          status: statusParam,
        });
        if (generation !== this.countryListFetchGeneration) {
          return;
        }
        this.countryListPage = targetPage;
        this.countryListTotal = 0;
        if (typeof res.data.count === "number") {
          this.countryListTotal = res.data.count;
        }
        this.countryList = (res.data.results ?? []).map((item) => ({
          ...item,
          country: item.country
            ? {
                ...item.country,
                flag_icon: item.country.flag_icon
                  ? `${configService.get("baseURL")}${item.country.flag_icon}`
                  : null,
              }
            : item.country,
        }));
      } catch (error) {
        if (generation !== this.countryListFetchGeneration) {
          return;
        }
        this.countryList = [];
        this.countryListTotal = 0;
      } finally {
        if (showLoader && generation === this.countryListFetchGeneration) {
          this.$loader.hide();
        }
      }
    },
    onCountryPageChange(page) {
      // Keep current rows visible — no full-page loader on pagination.
      return this.getOperatingCountry(page, undefined, { showLoader: false });
    },
    onCountrySearch(query) {
      let q = "";
      if (typeof query === "string") {
        q = query.trim();
      } else if (query !== null && query !== undefined) {
        q = String(query).trim();
      }
      this.countrySearchQuery = q;
      this.countryListPage = 1;
      return this.getOperatingCountry(1, undefined, { showLoader: false });
    },
    onCountryStatusFilter(filters) {
      const next = Array.isArray(filters)
        ? filters.filter((s) => typeof s === "string" && s.trim())
        : [];
      this.countryStatusFilters = next;
      this.countryListPage = 1;
      return this.getOperatingCountry(1, undefined, { showLoader: false });
    },
    async onReloadCountryList() {
      await this.getOperatingCountry(undefined, undefined, { showLoader: false });
      // Picker greyout can refresh in the background after the table updates.
      this.refreshOperatingCatalogCountryIds();
    },
    async onEnsureCountryCatalog() {
      // Keep Add Country snappy: only fetch the full catalog when empty, and
      // refresh operating-catalog ids in the background for picker greyout.
      const tasks = [this.refreshOperatingCatalogCountryIds()];
      if (!Array.isArray(this.dropdownCountryList) || this.dropdownCountryList.length === 0) {
        tasks.push(this.fetchDropdownCountryList());
      }
      await Promise.all(tasks);
    },
    async onAddOperatingCountry(country, listSearchFromTable) {
      const catalogCountryId = country?.id ?? country?.country?.id ?? null;
      if (catalogCountryId == null) {
        console.error("onAddOperatingCountry: missing catalog country id", country);
        return;
      }
      const fromPicker =
        (typeof country?.name === "string" && country.name.trim()) ||
        (typeof country?.country?.name === "string" && country.country.name.trim()) ||
        "";
      const hasTableSearchArg = arguments.length >= 2 && listSearchFromTable !== undefined;
      let snapshot = "";
      if (hasTableSearchArg) {
        snapshot = String(listSearchFromTable ?? "").trim();
      } else if (fromPicker) {
        snapshot = fromPicker;
      } else if (typeof this.countrySearchQuery === "string") {
        snapshot = this.countrySearchQuery.trim();
      }
      this.countrySearchQuery = snapshot;
      this.countryListPage = 1;

      let countryAdded = false;
      this.$loader.setAppLoading(true);
      this.$loader.show();
      try {
        await addOperatingCountry(this.accountID, {
          country: catalogCountryId,
          account: this.accountID,
        });
        countryAdded = true;
        await this.getOperatingCountry(1, snapshot, { showLoader: false });
        this.refreshOperatingCatalogCountryIds();
      } catch (error) {
        console.error("onAddOperatingCountry:", error);
      } finally {
        this.$loader.setAppLoading(false);
        this.$loader.hide();
      }
      if (countryAdded) {
        const currency =
          country?.currency ??
          this.dropdownCountryList.find((c) => String(c.id) === String(catalogCountryId))?.currency;
        await this.offerCurrencyNudge(currency);
      }
    },
    async findGlobalCurrency(currency) {
      const res = await getListOfCurrencies({ search: currency.code || currency.name });
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.results ?? [];
      return rows.find((row) => String(row?.id) === String(currency.id)) ?? null;
    },
    /** Rule 14: after a country is added, offer to add or reactivate its primary currency. */
    async offerCurrencyNudge(currency) {
      if (!this.ideaMdp506CurrencyNudgeEnabled || currency?.id == null) return;
      try {
        const res = await getOperatingCurrency(this.accountID, {
          size: 100,
          search: currency.name,
        });
        const existing = (res?.data?.results ?? []).find(
          (row) => String(this.catalogCurrencyIdFromOperatingRow(row)) === String(currency.id)
        );
        if (existing) {
          if (resolveOperatingCurrencyStatus(existing) === "retired") {
            this.currencyNudge = {
              show: true,
              mode: "reactivate",
              currency,
              operatingCurrencyId: existing.id,
            };
          }
          return;
        }
        const globalCurrency = await this.findGlobalCurrency(currency);
        if (globalCurrency?.active === true) {
          this.currencyNudge = { show: true, mode: "add", currency, operatingCurrencyId: null };
        }
      } catch (error) {
        console.error("offerCurrencyNudge:", error);
      }
    },
    declineCurrencyNudge() {
      this.currencyNudge = { show: false, mode: "add", currency: null, operatingCurrencyId: null };
    },
    async confirmCurrencyNudge() {
      const { mode, currency, operatingCurrencyId } = this.currencyNudge;
      this.declineCurrencyNudge();
      if (!currency) return;
      const isReactivate = mode === "reactivate";
      this.$loader.setAppLoading(true);
      try {
        if (isReactivate) {
          await reactivateOperatingCurrency(this.accountID, operatingCurrencyId);
        } else {
          await addOperatingCurrency(this.accountID, { currency: currency.id });
        }
        await this.getCurrencyList(1);
        this.refreshOperatingCatalogCurrencyIds();
        this.$notifications.success(
          `Operating currency ${currency.code || currency.name} ${
            isReactivate ? "reactivated" : "added"
          }`
        );
      } catch (error) {
        const data = error?.response?.data;
        this.$notifications.error(
          getOperatingErrorMessage(data?.code || data?.error_code || data?.detail?.code || "")
        );
        console.error("confirmCurrencyNudge:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async onSetWorkHours({ item, value }) {
      if (!item?.id) return;
      const next = Number(value);
      if (!Number.isFinite(next) || next === item.work_hours_per_year) return;
      this.$loader.setAppLoading(true);
      try {
        await updateOperatingCountry(this.accountID, item.id, {
          work_hours_per_year: next,
        });
        await this.onReloadCountryList();
      } catch (error) {
        console.error("onSetWorkHours:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async onCountryMenuAction({ item, action }) {
      if (!item?.id || !action) return;
      this.$loader.setAppLoading(true);
      try {
        if (action === "retire") {
          await partialUpdateOperatingCountry(this.accountID, item.id, {
            status: "retired",
            currency: item.country?.currency?.id,
          });
        } else if (action === "reactivate") {
          await partialUpdateOperatingCountry(this.accountID, item.id, {
            status: "active",
            currency: item.country?.currency?.id,
          });
        } else if (action === "setAsDefault") {
          // Clear other defaults on the loaded page, then set the chosen row.
          // Backend does not yet own a single set-default transaction.
          const otherDefaults = (this.countryList || []).filter(
            (row) => row.is_default && row.id !== item.id
          );
          await Promise.all(
            otherDefaults.map((row) =>
              partialUpdateOperatingCountry(this.accountID, row.id, {
                is_default: false,
              })
            )
          );
          await partialUpdateOperatingCountry(this.accountID, item.id, {
            is_default: true,
          });
        }
        await this.onReloadCountryList();
      } catch (error) {
        console.error("onCountryMenuAction:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async addCurrency(event, listSearchFromTable) {
      const hasTableSearchArg = arguments.length >= 2 && listSearchFromTable !== undefined;
      let snapshot = "";
      if (hasTableSearchArg) {
        snapshot = String(listSearchFromTable ?? "").trim();
      } else if (typeof this.currencySearchQuery === "string") {
        snapshot = this.currencySearchQuery.trim();
      }
      if (hasTableSearchArg) {
        this.currencySearchQuery = snapshot;
      }
      const catalogCurrencyId = this.catalogCurrencyIdFromAddCurrencyEvent(event);
      if (catalogCurrencyId === null) {
        console.error("addCurrency: missing catalog currency id on event", event);
        return;
      }
      this.currencyAddErrorCode = "";
      this.$loader.setAppLoading(true);
      this.$loader.show();
      const payload = {
        currency: catalogCurrencyId,
      };
      try {
        await addOperatingCurrency(this.accountID, payload);
        await this.getCurrencyList(1, snapshot);
        this.refreshOperatingCatalogCurrencyIds();
      } catch (error) {
        const code =
          error?.response?.data?.code ||
          error?.response?.data?.error_code ||
          error?.response?.data?.detail?.code ||
          "";
        if (code) {
          this.currencyAddErrorCode = code;
        }
        console.error("addCurrency:", error);
      } finally {
        this.$loader.setAppLoading(false);
        this.$loader.hide();
      }
    },
    async onEnsureCurrencyCatalog() {
      // Add Operating Currency modal loads the catalog via CurrencyPicker
      // scroll pagination (limit=10). operatingCatalogCurrencyIds already
      // greys out currencies that are operating — no bulk prefetch needed.
    },
    async onSetCurrencyRate({ item, rate }) {
      if (!item?.id) return;
      const next = Number(rate);
      if (!Number.isFinite(next)) return;
      this.$loader.setAppLoading(true);
      try {
        await setOperatingCurrencyRate(this.accountID, item.id, next);
        await this.getCurrencyList();
      } catch (error) {
        console.error("onSetCurrencyRate:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async onCurrencyRateModeChange({ item, mode }) {
      if (!item?.id || !mode) return;
      this.$loader.setAppLoading(true);
      try {
        await setOperatingCurrencyMode(this.accountID, item.id, mode);
        await this.getCurrencyList();
      } catch (error) {
        console.error("onCurrencyRateModeChange:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async onCurrencyMenuAction({ item, action }) {
      if (!item?.id || !action) return;
      this.$loader.setAppLoading(true);
      try {
        if (action === "retire") {
          await retireOperatingCurrency(this.accountID, item.id);
        } else if (action === "re-activate" || action === "reactivate") {
          await reactivateOperatingCurrency(this.accountID, item.id);
        } else if (action === "setAsReference") {
          await setOperatingCurrencyAsReference(this.accountID, item.id);
        } else if (action === "switchToCorporateRate") {
          await setOperatingCurrencyMode(this.accountID, item.id, "corporate");
        } else if (action === "switchToAutoRate") {
          await setOperatingCurrencyMode(this.accountID, item.id, "daily");
        } else if (action === "refreshExchangeRate") {
          try {
            await refreshOperatingCurrencyRate(this.accountID, item.id);
            this.$notifications.success("Exchange rate successfully updated");
          } catch (refreshError) {
            console.error("refreshExchangeRate:", refreshError);
            this.$notifications.error("Exchange rate failed to update");
          }
        }
        await this.getCurrencyList();
      } catch (error) {
        console.error("onCurrencyMenuAction:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async onBulkSetCurrencyMode({ mode }) {
      if (!mode) return;
      this.$loader.setAppLoading(true);
      try {
        await bulkSetOperatingCurrencyMode(this.accountID, mode);
        await this.getCurrencyList();
      } catch (error) {
        console.error("onBulkSetCurrencyMode:", error);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    handleSort(sortData) {
      this.sortOptions = {
        field: sortData.field,
        direction: sortData.direction,
      };
      this.paginationModel.page = 1;
      if (this.settingsUserV2Flags) {
        this.getUserListV2({ page: 1 });
      }
    },
    async openChatter(row) {
      try {
        this.openChatterModal.show = true;
        this.openChatterModal.chatterData = [];
        this.openChatterModal.accessId = row.user.data.id;
        this.openChatterModal.page = 1;
        this.openChatterModal.userName = `${row.user.data.user.first_name} ${row.user.data.user.last_name}`;
        this.openChatterModal.userAvatar = {
          picture: row.user.data?.avatar || "",
          firstName: row.user.data.user.first_name || "",
          lastName: row.user.data.user.last_name || "",
        };
        await this.getUserChatter();
      } catch (error) {
        console.error("Error opening chatter:", error);
      }
    },
    async getUserChatter() {
      try {
        const response = await getChatterList(
          this.accountID,
          this.openChatterModal.accessId,
          "account_permission",
          this.openChatterModal.page
        );
        if (response) {
          const chatterItems = transformChatterData(response.data.results);
          this.openChatterModal.chatterData.push(...chatterItems);
          this.openChatterModal.totalUserChatter = response.data.count;
        }
      } catch (error) {
        console.log("Error fetching user chatter:", error);
      }
    },
    closeUserChatterModal() {
      this.openChatterModal.show = false;
      this.openChatterModal.chatterData = [];
      this.openChatterModal.accessId = null;
      this.openChatterModal.page = 1;
      this.openChatterModal.totalUserChatter = 0;
      this.openChatterModal.userName = "";
      this.openChatterModal.userAvatar = {
        picture: "",
        firstName: "",
        lastName: "",
      };
    },
    async requestMoreUserChatter() {
      this.openChatterModal.page += 1;
      await this.getUserChatter();
    },
    openDetachEmployeeAccessDialog(row) {
      const data = row?.user?.data;
      const emp = data?.employee;
      if (!data?.has_employee || !emp?.id) {
        return;
      }
      const fn = emp.first_name || "";
      const ln = emp.last_name || "";
      const fullFromEmployee = `${fn} ${ln}`.trim();
      this.detachEmployeeAccessDialogFullName =
        fullFromEmployee || `${data.user?.first_name || ""} ${data.user?.last_name || ""}`.trim();
      this.detachEmployeeAccessDialogAvatar = {
        firstName: fn,
        lastName: ln,
        picture: emp.avatar || data.avatar || "",
      };
      this.detachEmployeeAccessDialogRow = row;
      this.showDetachEmployeeAccessDialog = true;
    },
    closeDetachEmployeeAccessDialog() {
      this.showDetachEmployeeAccessDialog = false;
      this.detachEmployeeAccessDialogRow = null;
      this.detachEmployeeAccessDialogFullName = "";
      this.detachEmployeeAccessDialogAvatar = {
        firstName: "",
        lastName: "",
        picture: "",
      };
    },
    onDetachEmployeeAccessDialogAction() {
      const employeeId = this.detachEmployeeAccessDialogEmployeeId;
      if (!this.accountID || !employeeId) {
        return;
      }
      this.$loader.setAppLoading(true);
      detachEmployeeAccess(this.accountID, employeeId)
        .then(async () => {
          this.closeDetachEmployeeAccessDialog();
          await this.loadUsers();
          requestAccessPermissionSync(this.$root);
        })
        .catch((err) => {
          console.error("Error detaching employee access:", err);
        })
        .finally(() => {
          this.$loader.setAppLoading(false);
        });
    },
  },
  components: {
    EXTooltip,
    HelpMenu,
    CancelInviteAdminUserPopup,
    ChangeUserAccessDialog,
    DetachEmployeeAccessDialog,
    EditPermission,
    ExCreateEmployeeWizard,
    SearchBar,
    SearchModal,
    SettingsMenu,
    DataTable,
    UserCard,
    UserPermissionBadge,
    UserActivity,
    AccessStatusBadge,
    UserStatusBadge,
    DropDownMenu,
    Icon,
    ExActionsPopUp,
    AppIntegrationsList,
    IntegrationsAccessPanel,
    SlackIntegrationCard,
    TeamsIntegrationCard,
    RenameUserNameModal,
    NoRecordFound,
    EmptyState,
    Button,
    ConfigurationView,
    OwnerShipModal,
    ChangeEmailModel,
    ChecklistDrawer,
  },
};
</script>
<style scoped>
.users-list-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #d92d20;
  border-radius: 12px;
  background-color: #ffffff;
  padding-bottom: 24px;
}

.users-list-error .empty-state {
  padding-bottom: 16px;
}

.invite-cell {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.invite-cell__primary {
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.invite-cell__secondary {
  color: #475467;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.pending-email {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  padding: 4px 8px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background-color: #ffffff;
}

.pending-email:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.pending-email__primary {
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.pending-email__secondary {
  color: #475467;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.with-employee {
  width: 100%;
  max-width: 100%;
}

.actions-cell-wrapper {
  display: inline-block !important;
  vertical-align: middle !important;
  line-height: normal !important;
}

.settings-icon-container {
  display: inline-block !important;
  vertical-align: middle !important;
  line-height: 0 !important;
}

.settings-icon-container .icon-svg {
  vertical-align: middle !important;
  display: inline-block !important;
}

.chatter-icon-container {
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chatter-icon-container:hover {
  background-color: #f2edff;
  box-shadow: 0 4px 4px 0 hsla(0, 0%, 40%, 0.1);
  transform: translateY(-1px);
}

.button-next {
  width: 87px;
  height: 44px;
  padding: 10px 16px 10px 16px;
  border-radius: 8px;
  border: solid 1px rgba(234, 236, 240, 1);
  background-color: rgba(242, 244, 247, 1);
  color: rgba(152, 162, 179, 1);
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
}

.text-black-600 {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  color: rgba(16, 24, 40, 1);
}

.text-black-500 {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(52, 64, 84, 1);
}
.v-text-field__slot {
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(102, 112, 133, 1);
}
.input {
  border-radius: 8px;
  margin: 0;
}

.pop-up {
  width: 480px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 1);
  position: relative;
}
.modal_header {
  z-index: 1;
  position: relative;
}
.modal_content {
  z-index: 1;
  position: relative;
}
.font-weight-500 {
  font-weight: 500;
}
.font-weight-400 {
  font-weight: 400;
}
.font-size-14 {
  font-size: 14px;
}
.text-disabled {
  color: rgba(102, 112, 133, 1);
}

.text-gray-dark {
  color: #666666;
}
.app-selector {
  border: 2px solid rgba(127, 86, 217, 1);
  cursor: pointer;
}
.v-input--selection-controls {
  padding-top: 0;
}
.v-application--is-ltr .v-input--selection-controls__input {
  margin: 0px;
}
.v-input--selection-controls__input .v-input__slot .v-label {
  padding-left: 1px !important;
}
.v-input--selection-controls__input .v-input__slot .theme--light .v-label {
  color: black;
  font-size: 14px;
}

::v-deep(.custom-bedge) {
  margin-top: 5px;
  margin-right: 5px !important;
}

::v-deep(.storybook-status-badge) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style>
.pop-up .v-input__slot {
  min-height: 44px !important;
}
.storybook-status-badge--suspended .custom-bedge.v-badge--dot .v-badge__badge {
  background-color: #b42318 !important;
}
.owner-badge-list {
  height: 60px !important;
}

.elevated-card.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03) !important;
}

.st-setting .container--fluid .v-item-group .v-window-item .v-sheet.v-card:not(.v-sheet--outlined) {
  margin: 20px;
}

.st-setting .horizontal-tab {
  width: 96%;
  margin-left: 20px;
}
.st-setting h1 {
  margin-left: 20px;
}

.icon-container.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  top: 15px;
  cursor: not-allowed;
  left: -8px;
}

::v-deep(.v-list-item) {
  margin-bottom: 300px !important;
}
</style>
